import React, { useState, useEffect, useContext, useRef } from "react"
import { useModuleContext } from "../shared/ModuleContext"
import { useEditorActions } from "../shared/useEditorActions"
import "../shared/components/module-components.css"
import { RadioButton } from "primereact/radiobutton"
import "primereact/resources/themes/saga-blue/theme.css"
import "primereact/resources/primereact.min.css"
import "primeicons/primeicons.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheckCircle, faSpinner } from "@fortawesome/pro-duotone-svg-icons"
import { faPlus, faEdit, faInfoCircle, faFilePdf, faDownload, faUpload, faCopy } from "@fortawesome/pro-regular-svg-icons"
import OutsideClickHandler from "react-outside-click-handler"
import { FirebaseContext } from "../../Firebase"
import { Modal as BootstrapModal, Form } from "react-bootstrap"
import { useStories } from "../useStories"
import { ModuleHomeDisplay } from "../../curriculum/ModuleHome"
import { navigate } from "../../../link"
import { FormattedMessage, useIntl } from "react-intl"
import { InputSwitch } from "primereact/inputswitch"
import useModuleDB from "../shared/useModuleDB"
import { useParams } from "@reach/router"
import { calculateModuleScore, getWordCount, hasHTMLContent } from "../shared/utils"
import { CircularProgressbar, buildStyles } from "react-circular-progressbar"
import { Dropdown } from "semantic-ui-react"
import DOMPurify from "dompurify"
import useSingleProgramDB from "../shared/useSingleProgramDB"
import { faPlusCircle, faGripVertical, faGripHorizontal } from "@fortawesome/pro-solid-svg-icons"
import ImageLoadError from "../../../../static/assets/images/image-load-error.png"
import CurriculumImageUploadSection from "../CurriculumImageUploadSection"
import CurriculumQuill from "../shared/quill/CurriculumQuill"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import { useReactToPrint } from "react-to-print"
import DeleteButton from "../shared/components/DeleteButton"
import DuplicateButton from "../shared/components/DuplicateButton"
import EditIcon from "../shared/components/EditIcon"
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons"
import HelpPopover from "../../generic/HelpPopover"

// drag and drop imports
import SortableContainer from "../../drag-and-drop/SortableContainer"
import useSortableItem from "../../drag-and-drop/useSortableItem"
import { closestCenter, pointerWithin } from "@dnd-kit/core"
import { verticalListSortingStrategy } from "@dnd-kit/sortable"
import { combineCollisionAlgorithms } from "../../drag-and-drop/customCollisionDetectionAlgorithms"

import { v4 as uuidv4 } from "uuid"

import Link from "../../../link"
import { WingsButton } from "@wingspans-ui"

/**
 * ========================================
 * WINGSPANS MODULE COMPONENT SYSTEM
 * ========================================
 *
 * This file contains the core component system for Wingspans educational modules.
 * It provides 14 different component types that can be combined to create interactive
 * learning experiences for students.
 *
 * COMPONENT TYPES:
 * ================
 *
 * QUESTION COMPONENTS (Interactive):
 * - QM1: Multiple Choice Questions (single selection)
 * - QO1: Open Response Questions (text input with word count validation)
 * - QC1: Checkbox Questions (multiple selection)
 * - QD1: Dropdown Questions (single selection via dropdown)
 * - IF2: File Upload Questions (file submission)
 *
 * INSTRUCTIONAL COMPONENTS (Display Only):
 * - IR1: Rich Text/HTML Content (formatted text, links, lists)
 * - II1: Image Components (with fallback error handling)
 * - IV1: Video Components (YouTube, Vimeo, Firebase Storage)
 * - IA1: Animation Components (Lottie animations)
 * - IA2: Completion Animation Components (celebration animations)
 * - IW1: Wingspans Story Components (curated stories with quotes)
 * - IW2: Wingspans Feature Components (platform integration buttons)
 * - IF1: File Download Components (downloadable resources)
 *
 * LAYOUT COMPONENTS (Containers):
 * - T1: Table Components (can contain other components in cells)
 *
 * KEY FEATURES:
 * =============
 * - Multilingual Support: Most components support English/Spanish content
 * - Drag & Drop: Components can be reordered in editor mode
 * - Response Tracking: Question components store and validate student responses
 * - Visual Feedback: Automatic grading with color-coded feedback
 * - Table Integration: Most components can be embedded within table cells
 * - Mode-Aware Rendering: Different behavior for STUDENT, EDITOR, and SUBMISSION modes
 * - UUID-based Identification: Each component has a unique identifier for state management
 *
 * RENDERING MODES:
 * ================
 * - STUDENT: Interactive mode where students can answer questions and view content
 * - EDITOR: Content creation mode with drag handles, edit buttons, and component insertion
 * - SUBMISSION: Review mode showing completed responses with grading feedback
 * - PREVIEW: Testing mode for content creators to experience the module as a student would,
 *   but without saving responses or affecting student data. Allows educators to test
 *   interactive elements and validate content before publishing.
 *
 * DATA FLOW:
 * ==========
 * Components receive data through props and dispatch responses via the module context.
 * Each component coordinates (sectionIndex, slideIndex, componentIndex) identify its
 * position in the module hierarchy for state management.
 *
 * For detailed component specifications, data structures, and examples, see:
 * MODULE_COMPONENT_SYSTEM.md in the project root
 *
 * ARCHITECTURE NOTES:
 * ===================
 * - Components are rendered conditionally based on mode (some hidden in SUBMISSION)
 * - Response validation occurs at the component level with visual feedback
 * - Multilingual content uses fallback patterns (Spanish -> English)
 * - File handling integrates with Firebase Storage for uploads/downloads
 * - Animation components require library maps for asset resolution
 * - Table components recursively render nested components with modified contexts
 */

/**
 * Helper function to get localized text with fallback to English
 * @param {string} locale - Current locale (e.g., "es-ES")
 * @param {string} spanishText - Spanish version of the text
 * @param {string} englishText - English version of the text (fallback)
 * @returns {string} - Localized text or English fallback
 */
const getLocalizedTextWithFallback = (locale, spanishText, englishText) => {
  if (locale === "es-ES" && spanishText && spanishText.trim() !== "") {
    return spanishText
  }
  return englishText
}

/**
 * Multiple Choice Question Component (QM1)
 *
 * Renders an interactive multiple-choice question with selectable options.
 * Supports both standard and table-embedded usage with localized content.
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Question data and configuration
 * @param {string} props.data.question - Question text in English
 * @param {string} [props.data.questionEs] - Question text in Spanish
 * @param {Array<Object>} props.data.options - Array of answer options
 * @param {string} props.data.options[].text - Option text in English
 * @param {string} [props.data.options[].textEs] - Option text in Spanish
 * @param {boolean} props.data.options[].isCorrect - Whether option is correct answer
 * @param {number|null} props.data.userResponse - Index of selected option (null if unanswered)
 * @param {boolean} [props.data.displayRow] - Whether to display options in a row layout
 * @param {Object} [props.coordinates={}] - Position coordinates in module structure
 * @param {number} props.coordinates.slideIndex - Index of containing slide
 * @param {number} props.coordinates.componentIndex - Index within slide components
 * @param {number} props.coordinates.sectionIndex - Index of containing section
 * @param {Object} [props.cellCoordinates={}] - Table cell coordinates if embedded in table
 * @param {boolean} [props.isTableComponent] - Whether component is embedded in a table
 *
 * @returns {JSX.Element} Interactive multiple choice question interface
 *
 * @example
 * // Basic multiple choice question
 * <MultipleChoiceComponent
 *   data={{
 *     question: "What is the capital of France?",
 *     questionEs: "¿Cuál es la capital de Francia?",
 *     options: [
 *       { text: "London", textEs: "Londres", isCorrect: false },
 *       { text: "Paris", textEs: "París", isCorrect: true },
 *       { text: "Berlin", textEs: "Berlín", isCorrect: false }
 *     ],
 *     userResponse: null,
 *     displayRow: false
 *   }}
 *   coordinates={{ slideIndex: 0, componentIndex: 1, sectionIndex: 0 }}
 * />
 *
 * @since 1.0.0
 * @memberof CurriculumComponents
 * @category InteractiveComponents
 */
const MultipleChoiceComponent = ({ data, coordinates = {}, cellCoordinates = {}, isTableComponent }) => {
  const { dispatch, moduleUI } = useModuleContext()
  const { mode } = moduleUI
  const { question, questionEs, options, userResponse, displayRow } = data
  const { slideIndex, componentIndex, sectionIndex } = coordinates
  const { locale } = useIntl()

  const handleOptionClick = (index) => {
    if (mode !== "STUDENT") return

    // block resubmission if answered
    if (!!userResponse) {
      return
    }

    dispatch({
      type: "RECORD_RESPONSE",
      slideIndex: slideIndex,
      componentIndex: componentIndex,
      sectionIndex: sectionIndex,
      isTableComponent: isTableComponent,
      cellCoordinates: cellCoordinates,
      payload: index,
    })
  }

  const renderSubmissionStyle = (index) => {
    // const isStudentMode = mode === "STUDENT";
    const isUserResponse = index === userResponse
    const isCorrectAnswer = options[index].isCorrect === true
    const hasUserResponded = userResponse !== null

    if (isUserResponse && isCorrectAnswer) {
      // user responded to the correct option
      return { backgroundColor: "#d4fb44", fontWeight: "bold" }
    } else if (isUserResponse && !isCorrectAnswer) {
      // user responded to the wrong option
      return { textDecoration: "line-through", textDecorationColor: "#94342a" }
    } else if (isCorrectAnswer && hasUserResponded) {
      // user had responded to the wrong option and we need to highlight the correct answer
      return { fontWeight: "bold", color: "green", border: "1px green transparent" }
    }
    return { fontWeight: "400", border: "1px solid transparent" }
  }

  const cleanLocalizedQuestionHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, questionEs, question))

  return (
    <>
      <div>
        {hasHTMLContent(cleanLocalizedQuestionHTML) && (
          <div dangerouslySetInnerHTML={{ __html: cleanLocalizedQuestionHTML }} className="module-component-question" />
        )}

        <div style={displayRow ? { display: "flex", flexDirection: "row", gap: "3rem" } : { display: "block" }}>
          {options.map((choice, index) => {
            const { option, optionEs } = choice

            const displayOption = getLocalizedTextWithFallback(locale, optionEs, option)

            return (
              <div style={{ padding: "2px", borderRadius: "3px", fontWeight: "light", ...renderSubmissionStyle(index) }} key={`question-${index}`}>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <RadioButton
                    disabled={userResponse !== null || mode !== "STUDENT" ? true : false}
                    // type="radio"
                    value={displayOption}
                    checked={index === userResponse}
                    onChange={() => handleOptionClick(index)}
                  />

                  <span>{displayOption}</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

/**
 * Renders a dropdown question component.
 * @param {Object} props.data - Data for the dropdown question.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Object} props.cellCoordinates - Coordinates of the component in a table cell (if applicable).
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @returns {JSX.Element} The dropdown question component.
 */
const DropdownComponent = ({ data, coordinates = {}, cellCoordinates = {}, isTableComponent }) => {
  const { dispatch, moduleUI } = useModuleContext()
  const { mode } = moduleUI
  const { question, questionEs, options, userResponse } = data
  const { slideIndex, componentIndex, sectionIndex } = coordinates
  const { locale, formatMessage } = useIntl()

  const handleOptionSelect = (selectedOptionIndex) => {
    if (mode !== "STUDENT") return
    // const selectedOptionIndex = parseInt(event.target.value, 10)

    dispatch({
      type: "RECORD_RESPONSE",
      slideIndex,
      componentIndex,
      sectionIndex,
      isTableComponent,
      cellCoordinates,
      payload: selectedOptionIndex,
    })
  }

  const cleanLocalizedQuestionHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, questionEs, question))

  const dropdownOptions = [
    // { key: "default", text: "Select an option", value: "", disabled: true },
    ...options.map((choice, index) => {
      const { option, optionEs } = choice
      const displayOption = getLocalizedTextWithFallback(locale, optionEs, option)

      return {
        key: `option-${index}`,
        text: displayOption,
        value: index,
      }
    }),
  ]

  return (
    <div className="module-component-dropdown">
      {hasHTMLContent(cleanLocalizedQuestionHTML) && (
        <div dangerouslySetInnerHTML={{ __html: cleanLocalizedQuestionHTML }} className="module-component-question" />
      )}
      {/* <Form.Control
        as="select"
        className="custom-select-dropdown"
        onChange={handleOptionSelect}
        value={userResponse !== null ? userResponse : ""}
        disabled={mode === "STUDENT" ? false : true}
      >
        <option value="" disabled>
          Select an option
        </option>
        {options.map((choice, index) => {
          const { option, optionEs } = choice

          const displayOption = locale === "es-ES" ? optionEs : option

          return (
            <option key={`option-${index}`} value={index}>
              {displayOption}
            </option>
          )
        })}
      </Form.Control> */}
      <Dropdown
        placeholder={formatMessage({ id: "curriculum-select-an-option", defaultMessage: "Select an option" })}
        selection
        options={dropdownOptions}
        value={userResponse !== null ? userResponse : ""}
        onChange={(e, { value }) => handleOptionSelect(value)}
        // disabled={mode !== "STUDENT"}
        className="custom-select-dropdown"
        style={{ minWidth: isTableComponent ? "98%" : "14em", maxWidth: "98%" }}
      />
    </div>
  )
}

/**
 * Renders an open-response question component. 
 * Displays a textarea for student input and validates word count if a minimum is specified.
 * Provides visual feedback on response completion and handles multilingual content.
 * If in SUBMISSION mode, displays the student's response in a read-only format with preserved formatting so that the full answer is visible on export
 * @param {Object} props.data - Data for the open-response question.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Object} props.cellCoordinates - Coordinates of the component in a table cell (if applicable).
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @returns {JSX.Element} The open-response question component.
 */
const OpenResponseComponent = ({ data, coordinates = {}, cellCoordinates = {}, isTableComponent }) => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const { mode } = moduleUI
  const { question, questionEs, userResponse, responseMinimum, placeholder } = data
  const { slideIndex, componentIndex, sectionIndex } = coordinates
  const { locale } = useIntl()
  const userWordCount = getWordCount(userResponse)

  const handleResponseChange = (e) => {
    if (mode !== "STUDENT") return

    dispatch({
      type: "RECORD_RESPONSE",
      slideIndex,
      componentIndex,
      sectionIndex,
      isTableComponent: isTableComponent,
      cellCoordinates: cellCoordinates,
      payload: e.target.value,
    })
  }

  const cleanLocalizedQuestionHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, questionEs, question))

  return (
    <div className="open-response">
      {hasHTMLContent(cleanLocalizedQuestionHTML) && (
        <div dangerouslySetInnerHTML={{ __html: cleanLocalizedQuestionHTML }} className="module-component-question" />
      )}

      {mode === "SUBMISSION" ? (
        <p
          style={{
            whiteSpace: "pre-wrap",
            border: "1px solid #d3d3d3",
            borderRadius: "3px",
            padding: "0.4375rem 1rem 0.375rem",
            marginBottom: isTableComponent ? "0" : "1rem",
            minHeight: "3rem",
            background: "#f8f8f8",
          }}
        >
          {userResponse}
        </p>
      ) : (
        <textarea
          disabled={mode === "STUDENT" ? false : true}
          value={userResponse || ""}
          onChange={handleResponseChange}
          rows={isTableComponent ? 2 : 4}
          style={{ width: "100%", marginTop: "10px" }}
          placeholder={placeholder && placeholder}
        />
      )}

      {mode !== "SUBMISSION" && (
        <>
          {responseMinimum ? (
            <label style={{ textAlign: "right" }}>
              {userWordCount >= responseMinimum ? <FontAwesomeIcon icon={faCheckCircle} color={"#50A097"} /> : ""} {responseMinimum}{" "}
              <FormattedMessage id="curriculum-word-min" defaultMessage="word min" />
            </label>
          ) : (
            <label style={{ textAlign: "right" }}>{userResponse?.length > 0 ? <FontAwesomeIcon icon={faCheckCircle} color={"#50A097"} /> : ""}</label>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Renders a Wingspans story component.
 * @param {Object} props.data - Data for the Wingspans story.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Object} props.cellCoordinates - Coordinates of the component in a table cell (if applicable).
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @returns {JSX.Element} The Wingspans story component.
 */
const WingspansStoryComponent = ({ data, coordinates = {}, cellCoordinates = {}, isTableComponent }) => {
  const { moduleUI } = useModuleContext()
  const { mode } = moduleUI
  const { quote, bodyHTML, quoteEs, bodyHTMLEs, link, imageSrc } = data
  const { locale } = useIntl()

  const cleanLocalizedBodyHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, bodyHTMLEs, bodyHTML))

  return (
    <div className="wingspans-story-component">
      <div>
        <img src={imageSrc} alt="Wingspans Story" />
      </div>
      <div>
        <div dangerouslySetInnerHTML={{ __html: cleanLocalizedBodyHTML }} className="module-component-question" />
        {quote?.length > 0 && <blockquote>"{getLocalizedTextWithFallback(locale, quoteEs, quote)}"</blockquote>}
        <a href={link} target="_blank" rel="noopener noreferrer">
          <FormattedMessage id="curriculum-click-here-to-go-to-story" defaultMessage="Click here to go to story" />
        </a>
      </div>
    </div>
  )
}

const WingspansFeatureComponent = ({ data, coordinates = {}, cellCoordinates = {}, isTableComponent }) => {
  const { moduleUI } = useModuleContext()
  const { mode } = moduleUI
  const { label, labelEs, href } = data
  const { locale } = useIntl()

  return (
    <div className="wingspans-link-component">
      <a href={href} target="_blank" rel="noopener noreferrer">
        <button className="button component-button component-button-ghost component-button-small">
          {locale === "es-ES" ? `${labelEs}` : `${label}`}
        </button>
      </a>
    </div>
  )
}

/**
 * Renders a rich-text component.
 * @param {Object} props.data - Data for the rich-text component.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @returns {JSX.Element} The rich-text component.
 */
const RichTextComponent = ({ data, coordinates = {} }) => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const { bodyHTML, bodyHTMLEs, src } = data
  const { slideIndex, componentIndex, sectionIndex } = coordinates
  const { locale } = useIntl()

  const addTargetBlank = (html) => {
    return html.replace(/<a(?![^>]*target=)([^>]*)href="([^"]+)"([^>]*)>/g, '<a$1href="$2"$3 target="_blank" rel="noopener noreferrer">')
  }

  const cleanLocalizedBodyHTML = addTargetBlank(DOMPurify.sanitize(getLocalizedTextWithFallback(locale, bodyHTMLEs, bodyHTML)))

  return (
    <div className="rich-text">
      <div dangerouslySetInnerHTML={{ __html: cleanLocalizedBodyHTML }} />
    </div>
  )
}

/**
 * Renders an image, but will hide the image or display a fallback image if the
 * image fails to load (for example, if a user deletes an image that they had
 * used in a module)
 * @param {string} errorImage - URL of an image to be displayed on error (optional)
 * @param {Object} props - All other props will be passed down to an img element
 * @returns {JSX.Element}
 */
export const ImageWithFallback = ({ errorImage, ...props }) => {
  return (
    <img
      {...props}
      onError={(event) => {
        event.currentTarget.onerror = null
        if (errorImage) {
          event.currentTarget.src = errorImage
        } else {
          event.currentTarget.style.display = "none"
        }
      }}
    />
  )
}

/**
 * Renders an image component.
 * @param {Object} props.data - Data for the image component.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @returns {JSX.Element} The image component.
 */
const ImageComponent = ({ data, coordinates = {} }) => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const { headingHTML, headingHTMLEs, src } = data
  const { slideIndex, componentIndex, sectionIndex } = coordinates
  const { locale } = useIntl()

  const cleanLocalizedHeadingHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, headingHTMLEs, headingHTML))

  return (
    <div className="image-component">
      {hasHTMLContent(cleanLocalizedHeadingHTML) && (
        <div dangerouslySetInnerHTML={{ __html: cleanLocalizedHeadingHTML }} className="module-component-question" />
      )}
      <ImageWithFallback src={src} alt="Image" errorImage={ImageLoadError} />
    </div>
  )
}

/**
 * Renders a video component.
 * @param {Object} props.data - Data for the video component.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @returns {JSX.Element} The video component.
 */
const VideoComponent = ({ data, coordinates = {} }) => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const { headingHTML, headingHTMLEs, vimeoSrc, youtubeSrc, firestoreSrc } = data
  const { slideIndex, componentIndex, sectionIndex } = coordinates
  const { locale } = useIntl()

  const cleanLocalizedHeadingHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, headingHTMLEs, headingHTML))

  const getVideoEmbedUrl = (url) => {
    if (youtubeSrc?.length > 0) {
      // Extract the video ID from the YouTube URL and format it for embedding
      // const url = "https://youtu.be/WvyvwlowHWM";
      const videoId = youtubeSrc.split("/").pop()
      return `https://www.youtube.com/embed/${videoId}`
    } else if (vimeoSrc?.length > 0) {
      // Extract the video ID from the Vimeo URL and format it for embedding
      // const url = "https://player.vimeo.com/video/1064681837?h=bd26e70add";

      const videoId = vimeoSrc.split("/").pop()
      return `https://player.vimeo.com/video/${videoId}`
    }
  }

  return (
    <div className="video-component">
      {hasHTMLContent(cleanLocalizedHeadingHTML) && (
        <div dangerouslySetInnerHTML={{ __html: cleanLocalizedHeadingHTML }} className="module-component-question" />
      )}
      <div className="">
        {firestoreSrc ? (
          <video controls style={{ width: "100%", height: "auto", display: "block" }}>
            <source src={firestoreSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <iframe
            title="story video"
            src={getVideoEmbedUrl()}
            frameBorder="0"
            allowFullScreen
            style={{ width: "100%", height: "auto", aspectRatio: "16 / 9", display: "block" }}
          ></iframe>
        )}
      </div>
    </div>
  )
}

/**
 * Renders an animation component.
 * @param {Object} props.data - Data for the image component.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @returns {JSX.Element} The image component.
 */
const AnimationComponent = ({ data, coordinates = {} }) => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const { mode } = moduleUI
  const { headingHTML, headingHTMLEs, fileName, loop, speed } = data
  const { slideIndex, componentIndex, sectionIndex } = coordinates
  const { locale } = useIntl()

  const cleanLocalizedHeadingHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, headingHTMLEs, headingHTML))

  const [animationData, setAnimationData] = useState(null)

  const animSRC = animationLibraryMap[fileName] || null

  if (mode === "SUBMISSION") {
    return <></>
  }

  return (
    <div className="image-component" key={`animation-${fileName}-${loop ? "loop" : "no-loop"}-${speed}`}>
      {hasHTMLContent(cleanLocalizedHeadingHTML) && (
        <div dangerouslySetInnerHTML={{ __html: cleanLocalizedHeadingHTML }} className="module-component-question" />
      )}
      <DotLottieReact src={animSRC} loop={loop} speed={speed} autoplay />{" "}
    </div>
  )
}

/**
 * Renders a completion animation component.
 * @param {Object} props.data - Data for the completion animation component.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @returns {JSX.Element} The completion animation component.
 */
const CompletionAnimationComponent = ({ data, coordinates = {} }) => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const { mode } = moduleUI
  const { headingHTML, headingHTMLEs, fileName, loop, speed } = data
  const { slideIndex, componentIndex, sectionIndex } = coordinates
  const { locale } = useIntl()

  const cleanLocalizedHeadingHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, headingHTMLEs, headingHTML))

  const [animationData, setAnimationData] = useState(null)

  const animSRC = completionAnimationLibraryMap[fileName] || null

  if (mode === "SUBMISSION") {
    return <></>
  }

  return (
    <div className="image-component" key={`completion-animation-${fileName}-${loop ? "loop" : "no-loop"}-${speed}`}>
      {hasHTMLContent(cleanLocalizedHeadingHTML) && (
        <div dangerouslySetInnerHTML={{ __html: cleanLocalizedHeadingHTML }} className="module-component-question" />
      )}
      <DotLottieReact src={animSRC} loop={loop} speed={speed} autoplay />{" "}
    </div>
  )
}

/**
 * Universal Module Component Renderer
 *
 * Central component that renders any type of module component based on its componentId.
 * Handles component-specific rendering, editing controls, drag-and-drop functionality,
 * and mode-aware interactions.
 *
 * This component serves as a factory that delegates to specific component renderers
 * based on the componentId (QM1, IR1, T1, etc.) and wraps them with common functionality
 * like editing controls, drag handles, and error boundaries.
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Complete component data object
 * @param {string} props.data.componentId - Component type identifier (QM1, IR1, QO1, etc.)
 * @param {string} props.data.uuid - Unique identifier for this component instance
 * @param {*} props.data.[...otherProps] - Component-specific data properties
 * @param {number} props.slideIndex - Zero-based index of containing slide
 * @param {number} props.componentIndex - Zero-based index within slide's component array
 * @param {number} props.sectionIndex - Zero-based index of containing section
 * @param {boolean} [props.disableDragging] - Whether to disable drag-and-drop temporarily
 *
 * @returns {JSX.Element} Rendered component with editing controls and drag functionality
 *
 * @example
 * // Rendering a multiple choice question
 * <ModuleComponent
 *   data={{
 *     componentId: "QM1",
 *     uuid: "comp-123",
 *     question: "What is photosynthesis?",
 *     options: [
 *       { text: "Process of making food", isCorrect: true },
 *       { text: "Process of breathing", isCorrect: false }
 *     ],
 *     userResponse: null
 *   }}
 *   slideIndex={0}
 *   componentIndex={1}
 *   sectionIndex={0}
 *   disableDragging={false}
 * />
 *
 * @example
 * // Rendering a rich text component
 * <ModuleComponent
 *   data={{
 *     componentId: "T1",
 *     uuid: "comp-456",
 *     contentHTML: "<p>This is rich text content</p>",
 *     contentHTMLEs: "<p>Este es contenido de texto enriquecido</p>"
 *   }}
 *   slideIndex={1}
 *   componentIndex={0}
 *   sectionIndex={0}
 * />
 *
 * @since 1.0.0
 * @memberof CurriculumComponents
 * @category CoreComponents
 */
const ModuleComponent = ({ data, slideIndex, componentIndex, sectionIndex, disableDragging }) => {
  const { moduleUI, moduleState, dispatch } = useModuleContext()
  const { mode } = moduleUI
  const { deleteComponent } = useEditorActions()
  // will have variable keys depending on type of component
  const { componentId } = data

  const [isHovered, setIsHovered] = useState(false)

  const [isEditing, setIsEditing] = useState(false)

  // compose coordinates here
  const coordinates = {
    sectionIndex: sectionIndex,
    slideIndex: slideIndex,
    componentIndex: componentIndex,
  }

  const thisComponent = moduleState.sections[sectionIndex]?.sectionSlides[slideIndex]?.slideContent[componentIndex]
  const componentUUID = thisComponent && thisComponent.uuid

  useEffect(() => {
    if (mode === "EDITOR" && thisComponent && !componentUUID) {
      dispatch({ type: "GENERATE_COMPONENT_UUID", sectionIndex, slideIndex, componentIndex })
    }
  }, [thisComponent, componentUUID, mode])

  const { itemProperties, handleProperties, isSorting, isDragging } = useSortableItem({
    id: componentUUID,
    disableDragging: mode !== "EDITOR" || disableDragging,
    separateDragHandle: true,
    scale: 1.01,
  })

  const renderComponent = () => {
    switch (componentId) {
      case "QM1":
        return <MultipleChoiceComponent data={data} coordinates={coordinates} />

      case "IR1":
        // dont display in the submission view
        if (mode === "SUBMISSION") return null
        return <RichTextComponent data={data} coordinates={coordinates} />

      case "QO1":
        return <OpenResponseComponent data={data} coordinates={coordinates} />

      case "QC1":
        return <CheckboxComponent data={data} coordinates={coordinates} />

      case "T1":
        return <TableComponent data={data} coordinates={coordinates} />

      case "II1":
        // dont display in the submission view
        if (mode === "SUBMISSION") return null
        return <ImageComponent data={data} coordinates={coordinates} />

      case "IA1":
        // dont display in the submission view
        if (mode === "SUBMISSION") return null
        return <AnimationComponent data={data} coordinates={coordinates} />

      case "IA2":
        // dont display in the submission view
        if (mode === "SUBMISSION") return null
        return <CompletionAnimationComponent data={data} coordinates={coordinates} />

      case "IV1":
        // dont display in the submission view
        if (mode === "SUBMISSION") return null
        return <VideoComponent data={data} coordinates={coordinates} />

      case "QD1":
        return <DropdownComponent data={data} coordinates={coordinates} />

      case "IW1":
        // dont display in the submission view
        if (mode === "SUBMISSION") return null
        return <WingspansStoryComponent data={data} coordinates={coordinates} />

      case "IW2":
        // dont display in the submission view
        if (mode === "SUBMISSION") return null
        return <WingspansFeatureComponent data={data} coordinates={coordinates} />
      case "IF1":
        return <FileDownloadComponent data={data} coordinates={coordinates} />
      case "IF2":
        return <FileUploadComponent data={data} coordinates={coordinates} />

      default:
        return null
    }
  }

  const handleDelete = () => {
    deleteComponent(coordinates)
  }

  const handleEditClick = () => {
    setIsEditing(true)
    setIsHovered(false)
  }

  let currentAction = "viewing"
  if (mode === "EDITOR") {
    if (isDragging) {
      currentAction = "dragging"
    } else if (isSorting) {
      currentAction = "sorting"
    } else if (isHovered) {
      currentAction = "hovering"
    }
  }

  // The div being reordered includes a component insertion area, but we only
  // want the component itself to have the "currently being dragged" styles
  const dragStyles = itemProperties.style || {}
  itemProperties.style = { zIndex: isDragging ? 1 : 0 }

  return (
    <div {...itemProperties}>
      {mode === "EDITOR" && (
        <ModuleComponentInsertionArea
          coordinates={{ ...coordinates, componentIndex: coordinates.componentIndex - 1 }}
          disable={isDragging || isSorting}
        />
      )}
      <div
        className={`module-component ${
          mode === "EDITOR" || mode === "SUBMISSION" ? "" : "module-component-student-view"
        } module-component-${currentAction}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={dragStyles}
      >
        {mode === "EDITOR" && (
          <>
            <div className="component-drag-handle" {...handleProperties}>
              {(isDragging || (isHovered && !isSorting)) && <FontAwesomeIcon icon={faGripHorizontal} size="xs" />}
            </div>
            <div className="component-editor-overlay">
              <button className="component-edit-button" onClick={() => handleEditClick()}>
                <EditIcon />
              </button>
              <ModuleComponentEditModal coordinates={coordinates} isEditing={isEditing} setIsEditing={setIsEditing} />
              <DeleteButton onClick={handleDelete} disabled={isDragging || isSorting} className="component-delete-button" />
            </div>
          </>
        )}

        {renderComponent()}
      </div>
    </div>
  )
}

/**
 * Renders a modal for editing an existing module component.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {boolean} props.isEditing - Whether the modal is in editing mode.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @returns {JSX.Element} The module component edit modal.
 */
const ModuleComponentEditModal = ({ coordinates, isEditing, setIsEditing }) => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const [isModalOpen, setIsModalOpen] = useState(true)

  const { sectionIndex, slideIndex, componentIndex } = coordinates || {}

  const currentComponent = moduleState.sections[sectionIndex]?.sectionSlides[slideIndex]?.slideContent[componentIndex] || {}

  const renderComponentCreator = () => {
    switch (currentComponent?.componentId) {
      case "QM1":
        return (
          <MultipleChoiceComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
        )
      case "IR1":
        return <RichTextComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "QO1":
        return <OpenResponseComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "QC1":
        return <CheckboxComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "QD1":
        return <DropdownComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IV1":
        return <VideoComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "II1":
        return <ImageComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IA1":
        return <AnimationComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IW1":
        return (
          <WingspansStoryComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
        )
      case "T1":
        return <TableComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IW2":
        return (
          <WingspansFeatureComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
        )
      case "IF1":
        return <FileDownloadComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IF2":
        return <FileUploadComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IA1":
        return <AnimationComponentCreator initialData={currentComponent} isEditing={true} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IA2":
        return (
          <CompletionAnimationComponentCreator
            initialData={currentComponent}
            isEditing={true}
            setIsEditing={setIsEditing}
            coordinates={coordinates}
          />
        )
      default:
        return null
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
      className="component-modal"
    >
      <Modal isOpen={isEditing} onClose={() => setIsEditing(false)} componentId={currentComponent?.componentId}>
        {renderComponentCreator()}
      </Modal>
    </div>
  )
}

/**
 * Renders a modal for creating a new module component.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {boolean} props.isEditing - Whether the modal is in editing mode.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @param {string} props.componentId - ID of the component type to create.
 * @returns {JSX.Element} The module component create modal.
 */
const ModuleComponentCreateModal = ({ coordinates, isEditing, setIsEditing, componentId }) => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const [isModalOpen, setIsModalOpen] = useState(true)

  const { sectionIndex, slideIndex, componentIndex } = coordinates || {}

  const renderComponentCreator = () => {
    switch (componentId) {
      case "QM1":
        return <MultipleChoiceComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IR1":
        return <RichTextComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "QO1":
        return <OpenResponseComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "QC1":
        return <CheckboxComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "QD1":
        return <DropdownComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IV1":
        return <VideoComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "II1":
        return <ImageComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IA1":
        return <AnimationComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IA2":
        return <CompletionAnimationComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IW1":
        return <WingspansStoryComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IW2":
        return <WingspansFeatureComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "T1":
        return <TableComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IF1":
        return <FileDownloadComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      case "IF2":
        return <FileUploadComponentCreator initialData={null} setIsEditing={setIsEditing} coordinates={coordinates} />
      default:
        return null
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "0",
        // height: "100vh"
      }}
      className="component-modal"
    >
      {/* <button onClick={() => setIsModalOpen(true)}>Edit</button> */}

      <Modal isOpen={isEditing} isNew={true} onClose={() => setIsEditing(false)} className="component-modal" componentId={componentId}>
        {renderComponentCreator()}
      </Modal>
    </div>
  )
}

/**
 * Renders a modal for adding or editing a component.
 * @param {boolean} props.isOpen - Whether the modal is open.
 * @param {Function} props.onClose - Function to close the modal.
 * @param {JSX.Element} props.children - Content of the modal.
 * @param {boolean} props.isNew - Whether the modal is for creating a new component.
 * @returns {JSX.Element} The modal.
 */
const Modal = ({ isOpen, onClose, children, isNew, componentId }) => {
  const getLabelByComponentId = (componentId) => {
    switch (componentId) {
      case "QM1":
        return "Multiple Choice Question"
      case "IR1":
        return "Rich Text"
      case "QO1":
        return "Open Response Question"
      case "QC1":
        return "Checkbox Question"
      case "QD1":
        return "Dropdown Question"
      case "IV1":
        return "Video"
      case "II1":
        return "Image"
      case "IA1":
        return "Animation"
      case "IA2":
        return "Completion Animation"
      case "IW1":
        return "Wingspans Story"
      case "IW2":
        return "Wingspans Feature"
      case "T1":
        return "Table"
      case "IF1":
        return "File Download"
      case "IF2":
        return "File Upload"
      default:
        return "Component"
    }
  }

  useEffect(() => {
    const handleModalChange = () => {
      const modals = document.querySelectorAll(".modal.show")
      if (modals.length > 1) {
        modals[modals.length - 2]?.classList.add("dimmed") // Dim the previous modal
      } else {
        document.querySelectorAll(".modal.dimmed").forEach((modal) => {
          modal.classList.remove("dimmed") // Restore when closing
        })
      }
    }

    const observer = new MutationObserver(handleModalChange)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [])

  if (!isOpen) return null

  return (
    <BootstrapModal show={isOpen} onHide={onClose} centered size="xl">
      <BootstrapModal.Header closeButton>
        <h4 style={{ marginBottom: "0" }}>{isNew ? `Add ${getLabelByComponentId(componentId)}` : `Edit ${getLabelByComponentId(componentId)}`}</h4>
      </BootstrapModal.Header>
      <BootstrapModal.Body>{children}</BootstrapModal.Body>
    </BootstrapModal>
  )
}

/**
 * Renders an insertion area for adding a new module component.
 * @param {Object} props.coordinates - Coordinates of the insertion area in the module.
 * @param {boolean} props.isTrailingSlide - Whether the insertion area is at the end of a slide.
 * @param {boolean} props.disable - Whether to temporarily disable insertion.
 * @returns {JSX.Element} The module component insertion area.
 */
const ModuleComponentInsertionArea = ({ coordinates, isTrailingSlide, disable }) => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const [isHovered, setIsHovered] = useState(false)
  const { openSidebarAndSetCooridinates, isComponentSidebarOpen, currentSectionIndex, currentSlideIndex, currentComponentIndex } = moduleUI

  const handleClick = () => {
    openSidebarAndSetCooridinates(coordinates)
  }

  const { sectionIndex, slideIndex, componentIndex } = coordinates

  let isActive =
    isComponentSidebarOpen && sectionIndex === currentSectionIndex && slideIndex === currentSlideIndex && componentIndex === currentComponentIndex

  const renderStyles = () => {
    if (isActive) {
      return {
        opacity: 1,
        height: "5rem",
        cursor: "pointer",
        padding: "3rem",
        border: "1px dashed #40777c",
        animation: "activeInsertionArea 1s infinite alternate",
      }
    } else if (isHovered && !isTrailingSlide) {
      // hover states
      return {
        opacity: 1,
        height: "1.5rem",
        background: "white",
        border: "1px dashed #40777c",
        padding: "1.5rem",
      }
    } else if (isTrailingSlide) {
      // hover states
      return {
        opacity: 1,
        height: "1.5rem",
        background: "white",
        border: isHovered ? "1px dashed #40777c" : "1px dashed #ccc",
        padding: "3rem",
      }
    } else {
      return {
        opacity: 0,
        height: "1.5rem",
        background: "transparent",
        border: "1px dashed transparent",
      }
    }
  }
  if (disable) {
    return <div style={{ height: "1.5rem" }} />
  }
  return (
    <div
      className="module-component module-component-insertion-area active"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...renderStyles(),
        textAlign: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontWeight: "bold",
        cursor: "pointer",
      }}
    >
      <FontAwesomeIcon icon={faPlus} size="2x" color="#40777c" />
    </div>
  )
}

/**
 * Renders a slide with its components.
 * @param {Object} props.data - Data for the slide, including its components.
 * @returns {JSX.Element} The slide.
 */
const Slide = ({ data }) => {
  const { slideContent, slideIndex, sectionIndex } = data
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const { mode } = moduleUI
  // const { openSidebarAndSetCooridinates, isComponentSidebarOpen, currentSectionIndex, currentSlideIndex, currentComponentIndex } = moduleUI

  const coordinates = {
    sectionIndex: sectionIndex,
    slideIndex: slideIndex,
    componentIndex: moduleState.sections[sectionIndex]?.sectionSlides[slideIndex]?.slideContent?.length + 1,
  }

  const componentUUIDs = moduleState.sections[sectionIndex]?.sectionSlides[slideIndex]?.slideContent.map((component) => {
    return component.uuid
  })
  const enableDragging = !!componentUUIDs && componentUUIDs.every((id) => id)
  const setComponentOrder = (newOrder) =>
    dispatch({ type: "UPDATE_COMPONENT_ORDER", sectionIndex: sectionIndex, slideIndex: slideIndex, payload: newOrder })

  const renderComponents = () => {
    return (slideContent || []).map((lessonComponent, componentIndex) => (
      <ModuleComponent
        key={`slide-${lessonComponent.uuid || componentIndex}`}
        data={lessonComponent}
        slideIndex={slideIndex}
        componentIndex={componentIndex}
        sectionIndex={sectionIndex}
        disableDragging={!enableDragging}
      />
    ))
  }

  // lesson component could have indexes abstracted
  return (
    <>
      {mode === "EDITOR" ? (
        <>
          <SortableContainer
            className="draggable-components-container"
            idList={enableDragging ? componentUUIDs : []}
            setOrder={enableDragging ? setComponentOrder : () => null}
            collisionDetectionAlgorithm={combineCollisionAlgorithms([pointerWithin, closestCenter])}
            sortingStrategy={verticalListSortingStrategy}
            indicatorPadding="0.75rem"
          >
            {renderComponents()}
          </SortableContainer>
          <ModuleComponentInsertionArea coordinates={coordinates} isTrailingSlide={true} />
        </>
      ) : (
        renderComponents()
      )}
    </>
  )
}

/**
 * Full Module View Component
 *
 * Renders the complete module with all sections and slides visible in a continuous
 * scrollable format. Primarily used for module overview and comprehensive review.
 *
 * @returns {JSX.Element} Complete module display with all sections
 *
 * @example
 * // Display entire module content
 * <FullModuleView />
 *
 * @since 1.0.0
 * @memberof CurriculumViews
 */
export const FullModuleView = () => {
  const { moduleState } = useModuleContext()
  const { sections } = moduleState

  return (
    <>
      {sections.map((data, sectionIndex) => (
        <Section data={{ ...data, sectionIndex: sectionIndex }} />
      ))}
    </>
  )
}

/**
 * Slide Module View Component
 *
 * Main module interface that displays one slide at a time with navigation controls.
 * Handles different states including active slides, completion page, overview editor,
 * and section overview editing. Core component for both student learning and
 * educator creation experiences.
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.isWingspansAdmin] - Whether current user has admin privileges
 *
 * @returns {JSX.Element} Single-slide view interface with navigation and editing controls
 *
 * @example
 * // Student learning interface
 * <SlideModuleView />
 *
 * @example
 * // Admin editing interface
 * <SlideModuleView isWingspansAdmin={true} />
 *
 * @since 1.0.0
 * @memberof CurriculumViews
 */
export const SlideModuleView = ({ isWingspansAdmin, canManageSchoolwideModules = false }) => {
  const { moduleState, moduleUI, moduleId } = useModuleContext()
  const { currentSlideIndex, currentSectionIndex, mode, completionStatus, isEditingOverview, isEditingSectionOverview, currentEditingSectionIndex } =
    moduleUI
  const { sections } = moduleState

  const slideData = sections[currentSectionIndex]?.sectionSlides[currentSlideIndex] || []

  // different views can appear in main area depending on what is happening
  const renderModuleView = () => {
    if (completionStatus === "completed" && mode === "STUDENT") {
      // render completion page for students
      return <CompletionPage />
    } else if (isEditingOverview && mode === "EDITOR") {
      // render editor page
      return <OverviewEditorPage isWingspansAdmin={isWingspansAdmin} canManageSchoolwideModules={canManageSchoolwideModules} />
    } else if (isEditingSectionOverview && mode === "EDITOR") {
      // render section overview editor page
      return <SectionOverviewSlide sectionIndex={currentEditingSectionIndex} />
      // return <SectionOverviewEditorPage sectionIndex={currentEditingSectionIndex} />
    } else {
      // default to slide view
      return (
        <Slide
          key={`slide-${moduleId}-${currentSectionIndex}-${currentSlideIndex}`}
          data={{
            ...slideData,
            slideIndex: currentSlideIndex,
            sectionIndex: currentSectionIndex,
          }}
        />
      )
    }
  }

  return <>{renderModuleView()}</>
}

const SectionOverviewSlide = ({ sectionIndex }) => {
  const { moduleState, dispatch } = useModuleContext()
  const { sections } = moduleState
  const section = sections[sectionIndex]

  const handleDeleteSection = () => {
    if (window.confirm("Are you sure you want to delete this section?")) {
      dispatch({ type: "DELETE_MODULE_SECTION", sectionIndex })
    }
  }

  if (!section) {
    return <></>
  }

  return (
    <div className="overview-editor-page">
      <div className="overview-editor-section" key={`overview-section-${sectionIndex}`}>
        <div className="overview-editor-section-header">
          <div style={{ width: "500px" }}>
            <label>Section Title</label>
            <input
              value={section.sectionTitle}
              onChange={(e) => dispatch({ type: "UPDATE_MODULE_SECTION_TITLE", sectionIndex: sectionIndex, payload: e.target.value })}
            />
          </div>

          <DeleteButton onClick={handleDeleteSection} className="section-delete-button" />
        </div>

        <label>Estimated Time</label>
        <input
          style={{ width: "500px" }}
          value={section.sectionTime}
          onChange={(e) => dispatch({ type: "UPDATE_MODULE_SECTION_ESTIMATED_TIME", sectionIndex: sectionIndex, payload: e.target.value })}
        />

        <label>What You Will Learn</label>
        {/* <ReactQuill
          value={section.sectionDescription}
          onChange={(value) => dispatch({ type: "UPDATE_MODULE_SECTION_LEARN", sectionIndex: sectionIndex, payload: value })}
          modules={{
            toolbar: [
              [{ header: [1, 2, 3, false] }],
              ["bold", "italic", "underline", "strike", "blockquote"],
              [{ list: "ordered" }, { list: "bullet" }],
              ["link", "image"],
              ["clean"],
            ],
          }}
          formats={["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "link", "image"]}
        /> */}
        <CurriculumQuill
          value={section.sectionDescription}
          onChange={(value) => dispatch({ type: "UPDATE_MODULE_SECTION_LEARN", sectionIndex: sectionIndex, payload: value })}
        />

        <label>Directions</label>
        {/* <ReactQuill
          value={section.sectionDirections}
          onChange={(value) => dispatch({ type: "UPDATE_MODULE_SECTION_DIRECTIONS", sectionIndex: sectionIndex, payload: value })}
          modules={{
            toolbar: [
              [{ header: [1, 2, 3, false] }],
              ["bold", "italic", "underline", "strike", "blockquote"],
              [{ list: "ordered" }, { list: "bullet" }],
              ["link", "image"],
              ["clean"],
            ],
          }}
          formats={["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "link", "image"]}
        /> */}
        <CurriculumQuill
          value={section.sectionDirections}
          onChange={(value) => dispatch({ type: "UPDATE_MODULE_SECTION_DIRECTIONS", sectionIndex: sectionIndex, payload: value })}
        />
      </div>
    </div>
  )
}

/**
 * Editor Footer Component
 *
 * Sticky footer bar for module creation interface providing save, delete, and
 * creation actions. Handles module persistence and provides visual feedback
 * for editing states and operations.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isNew - Whether creating a new module (true) or editing existing (false)
 * @param {boolean} props.isWingspansAdmin - Whether current user has admin privileges
 *
 * @returns {JSX.Element} Footer bar with action buttons and status indicators
 *
 * @example
 * // New module creation footer
 * <EditorFooter isNew={true} isWingspansAdmin={true} />
 *
 * @example
 * // Existing module editing footer
 * <EditorFooter isNew={false} isWingspansAdmin={false} />
 *
 * @since 1.0.0
 * @memberof CurriculumViews
 */
export const EditorFooter = ({ isNew, isWingspansAdmin, canManageSchoolwideModules = false }) => {
  const { firebase } = useContext(FirebaseContext)
  const { moduleId, moduleState, exportModuleState, importModuleState, accessType } = useModuleContext()
  const { createNewModule, saveUpdatedModule, deleteModule, isDeleting, isSaving } = useEditorActions(firebase)
  const hasSchoolwideModuleAccess = Boolean(isWingspansAdmin || canManageSchoolwideModules)
  const uploadInputRef = useRef(null)

  const hasRequiredSchoolSelected = () => {
    if (accessType !== "school") return true
    return Array.isArray(moduleState.schoolsWithAccess) && moduleState.schoolsWithAccess.length > 0
  }

  const handleCreateNewModule = async () => {
    if (isSaving) return

    if (!hasRequiredSchoolSelected()) {
      window.alert("Please select at least one school before saving a module with School access.")
      return
    }

    try {
      const res = await createNewModule({ accessType: accessType, isWingspansAdmin: hasSchoolwideModuleAccess })
      const createdId = res.data.res[0].id

      isWingspansAdmin ? navigate(`/admin/modules/edit/${createdId}`) : navigate(`/educator/assignments/modules/edit/${createdId}`)
    } catch (error) {
      console.error("Error saving new module", error)
    }
  }

  const handleSaveUpdatedModule = () => {
    if (!hasRequiredSchoolSelected()) {
      window.alert("Please select at least one school before saving a module with School access.")
      return
    }

    saveUpdatedModule({ accessType: accessType, isWingspansAdmin: hasSchoolwideModuleAccess })
  }

  return (
    <div className="module-creation-page-footer">
      {!isNew && (
        <WingsButton variant="tertiary" size="mini" loading={isDeleting} onClick={deleteModule}>
          Delete
        </WingsButton>
      )}

      {!isNew && (
        <a href={`/modules/${moduleId}`} target="_blank" rel="noopener noreferrer">
          <WingsButton variant="tertiary" size="mini">
            Visit Published Homepage
          </WingsButton>
        </a>
      )}

      <WingsButton variant="tertiary" size="mini" onClick={() => uploadInputRef.current?.click()}>
        Upload
      </WingsButton>
      <input type="file" id="moduleStateUpload" ref={uploadInputRef} accept=".json" onChange={importModuleState} hidden />

      <WingsButton variant="tertiary" size="mini" onClick={() => exportModuleState()}>
        Download
      </WingsButton>

      <WingsButton variant="primary" size="mini" loading={isSaving} onClick={isNew ? handleCreateNewModule : handleSaveUpdatedModule}>
        Save
      </WingsButton>
    </div>
  )
}


/**
 * Renders the overview editor page for editing module metadata and sections.
 * @returns {JSX.Element} The overview editor page.
 */
const OverviewEditorPage = ({ isWingspansAdmin, canManageSchoolwideModules = false }) => {
  const { moduleState, dispatch, moduleUI, accessType, setAccessType } = useModuleContext()
  const { firebase, schoolMemberships } = useContext(FirebaseContext)
  const { title, sections, skills = [], type } = moduleState
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [schoolOptions, setSchoolOptions] = useState([])
  const [selectedSchools, setSelectedSchools] = useState([])
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const hasAutoSelectedSchool = useRef(false)

  const handlePreviewToggle = () => {
    setIsPreviewing(!isPreviewing)
  }

  const handleSchoolSelection = (e, { value }) => {
    // Validate that all selected schools exist in the current school options
    const validSchoolCodes = schoolOptions.map((school) => school.schoolCode)
    const validatedSchools = value.filter((schoolCode) => validSchoolCodes.includes(schoolCode))

    if (validatedSchools.length !== value.length) {
      console.warn("Some selected schools do not exist in the current school list and were removed")
    }

    setSelectedSchools(validatedSchools)
  }

  // Convert stored school data to school codes if needed
  const convertStoredSchoolsToSchoolCodes = (storedSchools, schoolOptions) => {
    if (!storedSchools || !schoolOptions.length) return []

    // Check if stored values are already school codes
    const schoolCodes = schoolOptions.map((school) => school.schoolCode)
    const schoolIds = schoolOptions.map((school) => school.sqlId)

    // If all stored values are school codes, return as is
    if (storedSchools.every((school) => schoolCodes.includes(school))) {
      return storedSchools
    }

    // If stored values are IDs, convert them to school codes
    const convertedSchools = storedSchools
      .map((storedValue) => {
        const schoolOption = schoolOptions.find((school) => school.sqlId === storedValue || school.sqlId === parseInt(storedValue))
        return schoolOption ? schoolOption.schoolCode : null
      })
      .filter(Boolean) // Remove null values

    return convertedSchools
  }

  const loadSchools = async () => {
    try {
      const res = await firebase.getPartnerSchools()
      const options = res.data.map((school) => ({
        key: school.sch_code,
        sqlId: school.id,
        schoolCode: school.sch_code,
        text: school.name,
        value: school.sch_code, // Use school code as value instead of ID
      }))

      setSchoolOptions(options)

      // Convert and set initial selected schools after options are loaded
      const convertedSchools = convertStoredSchoolsToSchoolCodes(moduleState.schoolsWithAccess || [], options)
      setSelectedSchools(convertedSchools)
    } catch (error) {
      console.error("Error loading schools:", error)
    }
  }

  useEffect(() => {
    if (canManageSchoolwideModules) {
      loadSchools()
    }
  }, [canManageSchoolwideModules])

  // Mark that initial load is complete after first render
  useEffect(() => {
    setIsInitialLoad(false)
  }, [])

  // Reset selected schools when access type changes to non-school (but not on initial load)
  useEffect(() => {
    if (!isInitialLoad && accessType !== "school" && selectedSchools.length > 0) {
      setSelectedSchools([])
      dispatch({ type: "UPDATE_MODULE_SCHOOLS_WITH_ACCESS", payload: [] })
    }
  }, [accessType, dispatch, isInitialLoad])

  // Auto-select the school if the current user only belongs to a single school
  useEffect(() => {
    if (
      hasAutoSelectedSchool.current ||
      accessType !== "school" ||
      selectedSchools.length > 0 ||
      !schoolOptions.length ||
      !Array.isArray(schoolMemberships) ||
      schoolMemberships.length !== 1
    ) {
      return
    }

    const membershipSchoolCode = schoolMemberships[0].sch_code
    const matchedSchool = schoolOptions.find((school) => school.schoolCode === membershipSchoolCode)

    if (matchedSchool) {
      hasAutoSelectedSchool.current = true
      setSelectedSchools([matchedSchool.schoolCode])
    }
  }, [accessType, schoolOptions, schoolMemberships, selectedSchools])

  // Validate and update module state when selected schools change
  useEffect(() => {
    if (accessType === "school") {
      // Validate that all selected schools exist in the current school options
      const validSchoolCodes = schoolOptions.map((school) => school.schoolCode)
      const validatedSchools = selectedSchools.filter((schoolCode) => validSchoolCodes.includes(schoolCode))

      // If some schools were invalid, update the selection
      if (validatedSchools.length !== selectedSchools.length) {
        setSelectedSchools(validatedSchools)
      }

      dispatch({ type: "UPDATE_MODULE_SCHOOLS_WITH_ACCESS", payload: validatedSchools })
    }
  }, [selectedSchools, accessType, dispatch, schoolOptions])

  const allCategories = [
    { name: "Career and Self Development", key: "career_and_self_dev", type: "NACE" },
    { name: "Communication", key: "communication", type: "NACE" },
    { name: "Critical Thinking", key: "critical_thinking", type: "NACE" },
    { name: "Equity and Inclusion", key: "equity_and_inclusion", type: "NACE" },
    { name: "Leadership", key: "leadership", type: "NACE" },
    { name: "Professionalism", key: "professionalism", type: "NACE" },
    { name: "Teamwork", key: "teamwork", type: "NACE" },
    { name: "Technology", key: "technology", type: "NACE" },
    { name: "6th Grade", key: "6th_grade", type: "K-12" },
    { name: "7th Grade", key: "7th_grade", type: "K-12" },
    { name: "8th Grade", key: "8th_grade", type: "K-12" },
    { name: "9th Grade", key: "9th_grade", type: "K-12" },
    { name: "10th Grade", key: "10th_grade", type: "K-12" },
    { name: "11th Grade", key: "11th_grade", type: "K-12" },
    { name: "12th Grade", key: "12th_grade", type: "K-12" },
  ]

  const filteredCategories = allCategories.filter((category) => category.type === type)

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
        <div>
          <h5 style={{ marginBottom: "0" }}>Homepage</h5>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginLeft: "auto" }}>
          <label htmlFor="preview-switch">Preview Homepage</label>
          <InputSwitch inputId="preview-switch" checked={isPreviewing} onChange={handlePreviewToggle} />
        </div>
      </div>

      {isPreviewing ? (
        <ModuleHomeDisplay moduleData={{ module_json: moduleState }} moduleId={null} isPreview={true} />
      ) : (
        <div className="overview-editor-page">
          <div style={{ background: "#f7f7f7", padding: ".5rem 1rem", fontSize: "16px", borderRadius: ".5rem" }}>
            <p style={{ fontSize: "16px" }}>
              Welcome to the Wingspans Module Editor! Every module has a <b>homepage, sections,</b> and <b>slides</b> within a section. Think of a
              section as a chapter in a book and a slide as a page within the chapter.
              <br /> <br />
              <b>Slides</b> can be populated with with various types of content, including rich text, open response, images, videos, stories, and more
              <br /> <br />
              Add components by clicking on <b>Slide 1</b> in <b>Section Overview</b> and <b>[ + ]</b>. Once you add components, you can edit or
              delete them upon hover.
              <br /> <br />
              To get started, populate the information in Homepage. And you’re off!
            </p>
          </div>
          {canManageSchoolwideModules && (
            <>
              <label>
                Access Type{" "}
                <HelpPopover
                  title="Access Type"
                  content="Select the access type for the module. Private modules will be excluded from search results, and school modules will be accessible only to specific school subdomains."
                />
              </label>
              <Form.Control as="select" className="custom-select-dropdown" value={accessType} onChange={(e) => setAccessType(e.target.value)}>
                {isWingspansAdmin && <option value="public">Public</option>}
                <option value="private">Private</option>
                <option value="school">School</option>
              </Form.Control>

              {/* School Selection - Only shown when access type is "school" */}
              {accessType === "school" && (
                <>
                  <label>
                    School Selection{" "}
                    <HelpPopover
                      title="School Selection"
                      content="Select which schools will have access to this module. Only schools currently in the system can be selected, and school codes will be stored."
                    />
                  </label>
                  <Dropdown
                    placeholder="Select schools..."
                    fluid
                    multiple
                    selection
                    options={schoolOptions}
                    value={selectedSchools}
                    onChange={handleSchoolSelection}
                    style={{ marginBottom: "1rem" }}
                  />
                </>
              )}

              {/* add internal notes */}
              <label>
                Internal Notes{" "}
                <HelpPopover
                  title="Internal Notes"
                  content="Add any notes for the module. This will not be visible to users and is used to help identify modules"
                />
              </label>
              <textarea
                value={moduleState.internalNotes || ""}
                onChange={(e) => dispatch({ type: "UPDATE_MODULE_INTERNAL_NOTES", payload: e.target.value })}
                rows={4}
                style={{ width: "100%", marginBottom: "1rem" }}
              />

              <hr />
            </>
          )}
          <label>
            Title <HelpPopover title="Module Title" content="The title of your module. This will appear in multiple places" />
          </label>
          <input value={title} onChange={(e) => dispatch({ type: "UPDATE_MODULE_TITLE", payload: e.target.value })} />
          <label>
            Type <HelpPopover title="Type" content="The type of module, NACE for College Level and K-12 for middle / high schools" />
          </label>
          <Form.Control
            as="select"
            className="custom-select-dropdown"
            value={type}
            onChange={(e) => dispatch({ type: "UPDATE_MODULE_TYPE", payload: e.target.value })}
          >
            <option value="NACE">NACE</option>
            <option value="K-12">K-12</option>
          </Form.Control>
          <label>
            Category <HelpPopover title="Category" content="Select the category that best fits your module." />
          </label>{" "}
          <Form.Control
            as="select"
            className="custom-select-dropdown"
            value={moduleState.category}
            onChange={(e) => dispatch({ type: "UPDATE_MODULE_CATEGORY", payload: e.target.value })}
          >
            {filteredCategories.map((category) => (
              <option key={category.key} value={category.name}>
                {category.name}
              </option>
            ))}
          </Form.Control>
          <label>
            Banner Image{" "}
            <HelpPopover
              title="Banner Image"
              content="The banner image that appears at the top of the module homepage as well as in assignment cards"
            />
          </label>{" "}
          <CurriculumImageUploadSection
            imageURL={moduleState.image}
            updateImageURL={(newURL) => dispatch({ type: "UPDATE_MODULE_BANNER_IMAGE", payload: newURL })}
            imageClassName="banner-image-preview"
            imageType="Banner Image"
          />
          <label>
            Banner Color{" "}
            <HelpPopover title="Banner Color" content="Choose a color for the banner background, this appears in the homepage of the module" />
          </label>{" "}
          <Form.Control
            as="select"
            className="custom-select-dropdown"
            value={moduleState.bannerColor}
            onChange={(e) => dispatch({ type: "UPDATE_MODULE_BANNER_COLOR", payload: e.target.value })}
          >
            <option className="blue-gradient" value="blue">
              Blue
            </option>
            <option className="green-gradient" value="green">
              Green
            </option>
            <option className="red-gradient" value="red">
              Red
            </option>
            <option className="yellow-gradient" value="yellow">
              Yellow
            </option>
            <option className="purple-gradient" value="purple">
              Purple
            </option>
            <option className="orange-gradient" value="orange">
              Orange
            </option>
          </Form.Control>
          <label>
            Short Description{" "}
            <HelpPopover title="Short Description" content="A brief description of the module, appears in the homepage banner and assignment cards" />
          </label>{" "}
          <input
            value={moduleState.shortDescription}
            onChange={(e) => dispatch({ type: "UPDATE_MODULE_SHORT_DESCRIPTION", payload: e.target.value })}
          />
          <label>
            Why complete this module{" "}
            <HelpPopover
              title="Why Complete"
              content="Explain the purpose and benefits of completing this module, appears as main body text in homepage"
            />
          </label>{" "}
          {/* <ReactQuill
            value={moduleState.fullDescription}
            onChange={(value) => dispatch({ type: "UPDATE_MODULE_WHY_COMPLETE", payload: value })}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link", "image"],
                ["clean"],
              ],
            }}
            formats={["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "link", "image"]}
          /> */}
          <CurriculumQuill
            value={moduleState.fullDescription}
            onChange={(value) => dispatch({ type: "UPDATE_MODULE_WHY_COMPLETE", payload: value })}
          />
          <label>
            Estimated Time <HelpPopover title="Estimated Time" content="Provide an estimated time to complete the module ~ 5 mins" />
          </label>
          <input value={moduleState.time} onChange={(e) => dispatch({ type: "UPDATE_MODULE_ESTIMATED_TIME", payload: e.target.value })} />
          <label>
            Quote <HelpPopover title="Quote" content="Add an inspirational or relevant quote for the module. Appears in homepage" />
          </label>
          <input value={moduleState.quote} onChange={(e) => dispatch({ type: "UPDATE_MODULE_QUOTE", payload: e.target.value })} />
          <label>
            Quote Author <HelpPopover title="Quote Author" content="Provide the author of the quote. Appears in homepage" />
          </label>
          <input value={moduleState.quoteAuthor} onChange={(e) => dispatch({ type: "UPDATE_MODULE_QUOTE_AUTHOR", payload: e.target.value })} />
          <label>
            What You Will Learn (program description){" "}
            <HelpPopover
              title="What You Will Learn"
              content="Describe what users will gain from completion, appears only in program homepages that feature this module"
            />
          </label>
          {/* <ReactQuill
            value={moduleState.whatLearn}
            onChange={(value) => dispatch({ type: "UPDATE_MODULE_PROGRAM_LEARN", payload: value })}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link", "image"],
                ["clean"],
              ],
            }}
            formats={["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "link", "image"]}
          /> */}
          <CurriculumQuill value={moduleState.whatLearn} onChange={(value) => dispatch({ type: "UPDATE_MODULE_PROGRAM_LEARN", payload: value })} />
          <label>
            Directions (program description)
            <HelpPopover
              title="Directions"
              content="Provide instructions or directions for completing the module. Appears only in program homepages that feature this module"
            />
          </label>
          {/* <ReactQuill
            value={moduleState.directions}
            onChange={(value) => dispatch({ type: "UPDATE_MODULE_PROGRAM_DIRECTIONS", payload: value })}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link", "image"],
                ["clean"],
              ],
            }}
            formats={["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "link", "image"]}
          /> */}
          <CurriculumQuill
            value={moduleState.directions}
            onChange={(value) => dispatch({ type: "UPDATE_MODULE_PROGRAM_DIRECTIONS", payload: value })}
          />
          <label>
            Skills You Will Develop{" "}
            <HelpPopover
              title="Skills"
              content="List skills users will develop by completing this module, appears in box on homepage, click + add skill to add"
            />
          </label>
          {skills.map((skill, skillIndex) => (
            <div key={`skill-${skillIndex}`} style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
              <input value={skill} onChange={(e) => dispatch({ type: "UPDATE_MODULE_SKILL", payload: { skillIndex, payload: e.target.value } })} />
              <WingsButton variant="tertiary" size="mini" onClick={() => dispatch({ type: "REMOVE_MODULE_SKILL", payload: skillIndex })}>
                Remove
              </WingsButton>
            </div>
          ))}
          <WingsButton variant="secondary" size="mini" onClick={() => dispatch({ type: "ADD_MODULE_SKILL" })} style={{ marginTop: "1rem" }}>
            + Add Skill
          </WingsButton>
          {/* <hr /> */}
          {/* {sections.map((section, sectionIndex) => (
            <div className="overview-editor-section" key={`overview-section-${sectionIndex}`}>
              <div className="overview-editor-section-header">
                <div>
                  <label>Section Title</label>
                  <input
                    value={section.sectionTitle}
                    onChange={(e) => dispatch({ type: "UPDATE_MODULE_SECTION_TITLE", sectionIndex: sectionIndex, payload: e.target.value })}
                  />
                </div>

                <button style={{ marginLeft: "auto" }} onClick={() => dispatch({ type: "DELETE_MODULE_SECTION", sectionIndex })}>
                  <FontAwesomeIcon icon={faTrash} color={"#50A097"} />
                </button>
              </div>

              <label>What You Will Learn</label>
              <ReactQuill
                value={section.sectionDescription}
                onChange={(value) => dispatch({ type: "UPDATE_MODULE_SECTION_LEARN", sectionIndex: sectionIndex, payload: value })}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike", "blockquote"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link", "image"],
                    ["clean"],
                  ],
                }}
                formats={["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "link", "image"]}
              />

              <label>Directions</label>
              <ReactQuill
                value={section.sectionDirections}
                onChange={(value) => dispatch({ type: "UPDATE_MODULE_SECTION_DIRECTIONS", sectionIndex: sectionIndex, payload: value })}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike", "blockquote"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link", "image"],
                    ["clean"],
                  ],
                }}
                formats={["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "link", "image"]}
              />
            </div>
          ))}
          <button
            onClick={() => {
              dispatch({ type: "ADD_SECTION" })
            }}
            className="button component-button component-button-ghost component-button-small"
          >
            + Add Section
          </button> */}
        </div>
      )}
    </>
  )
}

/**
 * Completion Page that displays whenever a module has been completed by a student
 * Displays the score percentage and a congratulatory message based on the score
  1. If the score is above 70%, display a congratulatory message and the score percentage
  2. If the score is below 70%, display a message encouraging the student to try again and the score percentage
 * @returns {JSX.Element}
 */
const CompletionPage = () => {
  const { moduleState, programId } = useModuleContext()
  const { moduleId } = useParams()
  const { firebase } = useContext(FirebaseContext)
  const { userModuleData } = useModuleDB(moduleId, firebase, false)
  const { locale } = useIntl()

  // Fallback if API doesn't work to fetch score, recalculate score from current module
  const fallbackScore = calculateModuleScore(moduleState)

  const displayScore = userModuleData?.score || fallbackScore || 0

  const moduleTitle = moduleState.title

  // Localized text for English and Spanish
  const scoreBasedHeadingText =
    displayScore > 70 ? (locale === "es-ES" ? "¡Felicidades!" : "Congratulations!") : locale === "es-ES" ? "¡Buen esfuerzo!" : "Nice effort!"

  const { programModuleList = [], certificateId, programData } = useSingleProgramDB(programId, firebase)
  console.log('programData', programData)
  const currentModuleIndexInProgram = programModuleList.findIndex((module) => module.module_id === +moduleId)
  const upNextModuleId =
    currentModuleIndexInProgram + 1 < programModuleList.length ? programModuleList[currentModuleIndexInProgram + 1].module_id : null

  const scoredBasedSubHeadingText =
    displayScore > 70
      ? locale === "es-ES"
        ? `Has completado con éxito ${
            moduleState.titleEs ? `<b>${moduleState.titleEs}</b>` : `este módulo`
          } con una puntuación alta. <br/><br/>${certificateId && programId ? `<span>¡Has completado el programa <b>${programData.title}</b>!</span><br/><br/>` : ''} ¡Sigue con el buen trabajo!`
        : `You've successfully completed ${
            moduleTitle ? `<b>${moduleTitle}</b>` : `this module`
          } with a strong score of <b>${displayScore}%</b> and earned 10 pts! <br/><br/>${certificateId && programId ? `<span>You have completed the program <b>${programData.title}</b>!</span><br/><br/>` : ''} Keep up the great work!`
      : locale === "es-ES"
      ? `Has terminado ${moduleState.titleEs ? `<b>${moduleState.titleEs}</b>` : `este módulo`}. Intenta nuevamente si deseas mejorar tu puntuación.`
      : `You've finished ${
          moduleTitle ? `<b>${moduleTitle}</b>` : `this module`
        } with a score of ${displayScore}%. Try again if you’d like to improve your score.`

  const printRef = useRef()

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${userModuleData?.module_json?.title || "Student Module"}`,
  })

  const handlePdfExport = () => {
    if (typeof window !== "undefined") {
      handlePrint()
    }
  }

  return (
    <>
      <div className="module-completion-page">
        {displayScore > 70 && (
          <div className="module-completion-page-animation">
            <DotLottieReact src="/assets/animations/lottie/success_1.lottie" loop={false} autoplay style={{ width: "100%", height: "100%" }} />
          </div>
        )}

        {/* <div style={{ width: "250px", margin: "20px 40px" }}>
          <CircularProgressbar
            styles={buildStyles({
              strokeLinecap: "butt",
              textSize: "2rem",
              pathColor: "#58c8db",
              textColor: "#58c8db",
              trailColor: "#C8EDF3",
            })}
            strokeWidth={12}
            value={displayScore}
            maxValue={100}
            text={`${displayScore}%`}
          />
        </div> */}
        <div className="module-completion-page-body">
          <h1
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(scoreBasedHeadingText),
            }}
          ></h1>

          {/* Success Animation */}

          <h5
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(scoredBasedSubHeadingText),
            }}
          ></h5>

          <div className="module-completion-page-actions">
            {certificateId && programId && (
              <Link to={`/certificates/${certificateId}`}>
                <WingsButton variant="secondary" size="mini" iconName="certificate">
                  Program Certificate
                </WingsButton>
              </Link>
            )}

            <WingsButton variant="primary" size="mini" iconName="download" onClick={handlePdfExport}>
              Export PDF
            </WingsButton>
          </div>

          {/* Hidden print content */}
          <div style={{ display: "none" }}>
            <div ref={printRef}>
              <FullModuleView selectedModuleJson={userModuleData} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const Section = ({ data }) => {
  const { locale } = useIntl()
  const { sectionTitle, sectionTitleEs, sectionSlides, sectionIndex } = data
  return (
    <>
      <h4>{getLocalizedTextWithFallback(locale, sectionTitleEs, sectionTitle)}</h4>
      {sectionSlides.map((slideData, slideIndex) => (
        <Slide
          data={{
            ...slideData,
            slideIndex: slideIndex,
            sectionIndex: sectionIndex,
          }}
        />
      ))}

      <hr />
    </>
  )
}

/**
 * Outline Editor Sidebar Component
 *
 * Left sidebar for module creation interface providing hierarchical navigation
 * and structure management. Allows educators to add/remove sections and slides,
 * navigate between different parts of the module, and manage overall module structure.
 *
 * Features:
 * - Module overview editing
 * - Section management (add, remove, navigate)
 * - Slide management (add, remove, navigate)
 * - Visual indication of current location
 * - Drag-and-drop reordering (when integrated)
 *
 * @returns {JSX.Element} Hierarchical outline editor interface
 *
 * @example
 * // Used in module creation layout
 * <div className="module-creation-sidebar">
 *   <OutlineEditor />
 * </div>
 *
 * @since 1.0.0
 * @memberof CurriculumViews
 */
export const OutlineEditor = () => {
  const { moduleState, moduleUI, dispatch } = useModuleContext()
  const { sections } = moduleState
  const { goToEditorOverview, isEditingOverview, goToLastSection } = moduleUI

  return (
    <div className="outline-editor">
      <button
        onClick={() => goToEditorOverview()}
        className={`outline-editor-overview ${isEditingOverview ? "active" : ""}`}
        // style={{ cursor: "pointer", background: isEditingOverview ? "#F7F7F7" : "white", fontWeight: isEditingOverview ? "bold" : "normal" }}
      >
        <EditIcon active={isEditingOverview} className="homepage-edit-icon" />
        <h6 style={{ fontWeight: isEditingOverview ? "bold" : "600" }}>Homepage</h6>
      </button>

      {sections.map((section, sectionIndex) => (
        <OutlineEditorSection section={section} sectionIndex={sectionIndex} key={section.uuid} />
      ))}

      <div className="outline-editor-add-section">
        <WingsButton
          variant="secondary"
          size="mini"
          onClick={() => {
            dispatch({ type: "ADD_SECTION" })
            goToLastSection()
          }}
        >
          + Section
        </WingsButton>
      </div>
    </div>
  )
}

/**
 * Renders a section in the sidebar outline editor with slides and an option to add new slides.
 *
 * @param {Object} props.section - Section data containing title and slides, all data from module json in section
 * @param {number} props.sectionIndex - Index of the section.
 * @returns {JSX.Element} The section editor.
 */
const OutlineEditorSection = ({ section, sectionIndex }) => {
  const { dispatch, moduleUI, moduleState } = useModuleContext()
  const { goToLastSlideInSection, setIsEditingSectionOverview, setIsEditingOverview, setCurrentEditingSectionIndex, goToSectionAndSlideAsAdmin } =
    moduleUI
  const { sectionTitle = "Untitled Section", sectionSlides } = section

  // const handleTitleChange = (e) => {
  //   const newTitle = e.target.value
  //   dispatch({ type: "UPDATE_SECTION_TITLE", sectionIndex, payload: newTitle })
  // }

  const handleNewSlide = () => {
    dispatch({ type: "ADD_SLIDE", sectionIndex: sectionIndex })
    goToLastSlideInSection(sectionIndex)
  }

  // const handleSectionDeletion = () => {
  //   //confirm deletion
  //   if (typeof window !== "undefined" && window.confirm("Are you sure you want to delete this section?")) {
  //     dispatch({ type: "DELETE_MODULE_SECTION", sectionIndex })
  //   }
  // }

  const handleEditSection = () => {
    setIsEditingOverview(false)
    setIsEditingSectionOverview(true)
    setCurrentEditingSectionIndex(sectionIndex)
  }

  // After sections are moved, will scroll the sidebar to try to keep the moved
  // section in the same position on the screen. Assumes the sidebar sections
  // have no margins between them.
  const moveSection = (e, newIndex) => {
    e.stopPropagation() // prevent click from bubbling up to slide edit button

    let newYCoordinate = 0
    let sidebarElement = undefined
    if (typeof document !== "undefined") {
      sidebarElement = document.getElementById("module-creation-sidebar")
      const currentYCoordinate = sidebarElement.scrollTop
      const targetId = moduleState.sections[newIndex].uuid
      const targetSection = document.getElementById(targetId)
      let targetHeight = targetSection.getBoundingClientRect().height
      if (newIndex < sectionIndex) {
        targetHeight *= -1
      }
      newYCoordinate = currentYCoordinate + targetHeight
    }

    dispatch({ type: "SWAP_SECTION_POSITIONS", sectionIndex: sectionIndex, payload: newIndex })
    setIsEditingOverview(false)
    setIsEditingSectionOverview(true)
    setCurrentEditingSectionIndex(newIndex)

    if (typeof document !== "undefined") {
      sidebarElement.scroll(0, newYCoordinate)
    }
  }

  const isActive = moduleUI.isEditingSectionOverview && moduleUI.currentEditingSectionIndex === sectionIndex

  const slideUUIDs = sectionSlides.map((slide) => slide.uuid)
  const setSlideOrder = (newOrder) => dispatch({ type: "UPDATE_SLIDE_ORDER", sectionIndex: sectionIndex, payload: newOrder })
  const onDrag = (draggingId) => {
    const draggingIndex = sectionSlides.findIndex((slide) => slide.uuid === draggingId)
    if (draggingIndex !== -1) {
      goToSectionAndSlideAsAdmin(sectionIndex, draggingIndex)
    }
  }
  const onDrop = (draggingId, newOrder) => {
    const droppingIndex = newOrder.findIndex((id) => id === draggingId)
    if (droppingIndex !== -1) {
      goToSectionAndSlideAsAdmin(sectionIndex, droppingIndex)
    }
  }

  return (
    <div className="outline-section" id={section.uuid}>
      <button className={`outline-section-overview ${isActive ? "active" : ""}`} onClick={handleEditSection}>
        <div className="section-move-buttons-container">
          <button className="section-move-button" disabled={sectionIndex === 0} onClick={(e) => moveSection(e, sectionIndex - 1)}>
            <FontAwesomeIcon icon={faChevronUp} size="xs" />
          </button>
          <button
            className="section-move-button"
            disabled={sectionIndex === moduleState.sections.length - 1}
            onClick={(e) => moveSection(e, sectionIndex + 1)}
          >
            <FontAwesomeIcon icon={faChevronDown} size="xs" />
          </button>
        </div>
        <EditIcon active={isActive} className="section-edit-icon" />

        {/* <input
          type="text"
          value={sectionTitle}
          onChange={handleTitleChange}
          placeholder="Enter section title"
          className="editable-section-title"
          style={{
            marginBottom: "0.5rem",
            width: "100%",
            padding: "0.5rem",
          }}
        /> */}
        <div className={`outline-section-overview-title`}>{sectionTitle || "Section Overview"}</div>
      </button>
      <SortableContainer
        className="module-section-slides-container"
        idList={slideUUIDs}
        setOrder={setSlideOrder}
        collisionDetectionAlgorithm={closestCenter}
        sortingStrategy={verticalListSortingStrategy}
        indicatorPadding="0.25rem"
        onDrag={onDrag}
        onDrop={onDrop}
      >
        {sectionSlides.map((slide, slideIndex) => (
          <OutlineEditorSlide
            key={`outline-section-slide-${slide.uuid || slideIndex}`}
            sectionIndex={sectionIndex}
            slide={slide}
            slideIndex={slideIndex}
          />
        ))}
      </SortableContainer>
      <button
        onClick={handleNewSlide}
        style={{
          paddingLeft: "3.25rem",
          display: "flex",
          flexDirection: "row",
          gap: ".8rem",
          alignItems: "center",
          fontWeight: 500,
          color: "#363636",
          borderRadius: ".5rem",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          width: "100%",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgb(64, 119, 124)"
          e.currentTarget.style.color = "white"
          e.currentTarget.querySelector("svg").style.color = "white"
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent"
          e.currentTarget.style.color = "#363636"
          e.currentTarget.querySelector("svg").style.color = "rgb(64, 119, 124)"
        }}
      >
        <div>
          <div>
            <FontAwesomeIcon icon={faPlusCircle} color="rgb(64, 119, 124)" />
          </div>
        </div>
        Slide
      </button>
    </div>
  )
}
/**
 * Renders a slide in the outline editor with options to edit and delete the slide.
 * @param {Object} props.sectionIndex - Index of the section.
 * @param {Object} props.slideIndex - Index of the slide.
 * @returns {JSX.Element} The slide editor.
 */
const OutlineEditorSlide = ({ sectionIndex, slideIndex }) => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const { currentSectionIndex, currentSlideIndex, goToSectionAndSlideAsAdmin, isEditingOverview, isEditingSectionOverview } = moduleUI

  const isActive = !isEditingOverview && !isEditingSectionOverview && sectionIndex === currentSectionIndex && slideIndex === currentSlideIndex

  const slideUUID = moduleState.sections[sectionIndex].sectionSlides[slideIndex].uuid
  const { itemProperties, handleProperties, isSorting, isDragging } = useSortableItem({ id: slideUUID, separateDragHandle: true, scale: 1 })
  const currentlyDragging = isSorting || isDragging

  const handleDelete = () => {
    const slideHasContent = moduleState.sections[sectionIndex]?.sectionSlides[slideIndex].slideContent.length > 0

    if (slideHasContent) {
      if (typeof window !== "undefined" && window.confirm("This slide contains content. Are you sure you want to delete it?")) {
        dispatch({ type: "DELETE_SLIDE", sectionIndex, slideIndex })
      }
    } else {
      dispatch({ type: "DELETE_SLIDE", sectionIndex, slideIndex })
    }
  }

  const handleDuplicate = () => {
    dispatch({ type: "DUPLICATE_SLIDE", sectionIndex, slideIndex })
  }

  return (
    <button
      key={slideUUID || slideIndex}
      className={`select-slide-tab ${isActive ? "active-slide-tab" : "inactive-slide-tab"}`}
      onClick={() => goToSectionAndSlideAsAdmin(sectionIndex, slideIndex)}
      disabled={currentlyDragging}
      {...itemProperties}
    >
      <button {...handleProperties} className="slide-drag-handle">
        <FontAwesomeIcon icon={faGripVertical} color={isDragging ? "#40777C" : ""} size="xs" />
      </button>
      <EditIcon active={isActive} disabled={currentlyDragging} className="slide-edit-icon" />
      {`Slide ${slideIndex + 1}`}
      <div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem" }}>
        <DuplicateButton onClick={handleDuplicate} disabled={currentlyDragging} className="slide-duplicate-button" />
        <DeleteButton onClick={handleDelete} disabled={currentlyDragging} className="slide-delete-button" />
      </div>
    </button>
  )
}

/**
 * Component Editor Sidebar
 *
 * Right sidebar for module creation interface providing component library and
 * creation tools. Allows educators to browse available component types and
 * add them to the current slide.
 *
 * Features:
 * - Component type browser with visual previews
 * - Component creation modals
 * - Component configuration interfaces
 * - Integration with component insertion system
 *
 * Supports all 14 component types:
 * - QM1: Multiple Choice Questions
 * - IR1: Dropdown Questions
 * - QO1: Open Response Questions
 * - T1: Rich Text Content
 * - II1: Images
 * - IV1: Videos
 * - IA1: Animations
 * - IA2: Completion Animations
 * - QC1: Checkbox Questions
 * - QD1: Table Components
 * - IW1: File Downloads
 * - IW2: File Uploads
 * - IF1: Wingspans Stories
 * - IF2: Wingspans Features
 *
 * @returns {JSX.Element} Component library sidebar with creation interface
 *
 * @example
 * // Used in module creation layout
 * <div className="module-creation-component-sidebar">
 *   <ComponentEditor />
 * </div>
 *
 * @since 1.0.0
 * @memberof CurriculumViews
 */
export const ComponentEditor = () => {
  const { moduleUI } = useModuleContext()
  const { isComponentSidebarOpen, currentSectionIndex, currentSlideIndex, currentComponentIndex, setIsComponentSidebarOpen } = moduleUI
  const [isEditing, setIsEditing] = useState(false)
  const [selectedComponentId, setSelectedComponentId] = useState(null)

  const coordinates = {
    sectionIndex: currentSectionIndex,
    slideIndex: currentSlideIndex,
    componentIndex: currentComponentIndex,
  }

  const handleSelectComponentType = (componentId) => {
    setSelectedComponentId(componentId)
    setIsEditing(true)
    setIsComponentSidebarOpen(false)
  }

  const renderSampleComponent = (componentId) => {
    switch (componentId) {
      case "QM1":
        return
      case "IR1":
        return
      case "QO1":
        return
      case "QC1":
        return
      case "QD1":
        return
      case "IV1":
        return
      case "II1":
        return
      case "IW1":
        return
      default:
        return null
    }
  }

  return (
    <OutsideClickHandler onOutsideClick={() => setIsComponentSidebarOpen(false)}>
      <div className="component-selection-sidebar" style={{ display: isComponentSidebarOpen ? "block" : "none" }}>
        <label>Add Component</label>
        <ModuleComponentCreateModal coordinates={coordinates} isEditing={isEditing} setIsEditing={setIsEditing} componentId={selectedComponentId} />
        <div className="component-selection-sidebar-type-list">
          <button onClick={() => handleSelectComponentType("IR1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} />{" "}
              </div>
            </div>{" "}
            Rich Text
          </button>
          <button onClick={() => handleSelectComponentType("QM1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Multiple Choice
          </button>
          <button onClick={() => handleSelectComponentType("QO1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Open Response
          </button>
          <button onClick={() => handleSelectComponentType("QC1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Checkbox
          </button>
          <button onClick={() => handleSelectComponentType("QD1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Dropdown
          </button>
          <button onClick={() => handleSelectComponentType("IV1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Video
          </button>
          <button onClick={() => handleSelectComponentType("II1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Image
          </button>
          <button onClick={() => handleSelectComponentType("IA1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Animation
          </button>
          <button onClick={() => handleSelectComponentType("IA2")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Completion Animation
          </button>
          <button onClick={() => handleSelectComponentType("IW1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Wingspans Story
          </button>
          <button onClick={() => handleSelectComponentType("IW2")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Wingspans Feature
          </button>
          <button onClick={() => handleSelectComponentType("T1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            Table
          </button>
          <button onClick={() => handleSelectComponentType("IF1")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            File
          </button>
          <button onClick={() => handleSelectComponentType("IF2")}>
            <div>
              <div>
                <FontAwesomeIcon icon={faPlusCircle} color="#40777c" />{" "}
              </div>
            </div>{" "}
            File Upload
          </button>
        </div>

        {/* {renderSampleComponent("QM1")} */}
      </div>
    </OutsideClickHandler>
  )
}

/**
 * Renders the RichTextComponentCreator, which allows users to create or edit a rich-text component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @param {Function} props.handleTableCellSubmit - Function to handle table cell submission.
 * @returns {JSX.Element} The rich-text component creator.
 */
const RichTextComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing, isTableComponent, handleTableCellSubmit }) => {
  const { addComponent, updateComponent } = useEditorActions()
  // support initial data so that we can edit components
  const { bodyHTML } = initialData || {}

  const [value, setValue] = useState(bodyHTML ?? "")

  const componentPayload = {
    componentId: "IR1",
    type: "INSTRUCTIONAL",
    bodyHTML: value,
    bodyHTMLEs: "",
  }

  const insertComponentAtLocation = () => {
    isEditing ? updateComponent(componentPayload, coordinates) : addComponent(componentPayload)
    setIsEditing(false)
  }

  return (
    <>
      <label>
        Text
        <HelpPopover
          title="Text"
          content="Enter the rich text content to display to students. You can use formatting, links, multiple paragraphs, and lists"
        />
      </label>
      <CurriculumQuill value={value} onChange={setValue} />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={isTableComponent ? () => handleTableCellSubmit(componentPayload) : () => insertComponentAtLocation()}
          style={{ marginTop: "1rem" }}
          // disabled={!value}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </>
  )
}

/**
 * Renders the VideoComponentCreator, which allows users to create or edit a video component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @returns {JSX.Element} The video component creator.
 */
const VideoComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing }) => {
  const { headingHTML, vimeoSrc, youtubeSrc, firestoreSrc, uuid } = initialData || {}
  const { addComponent, updateComponent } = useEditorActions()
  const { firebase } = useContext(FirebaseContext)

  const [componentData, setComponentData] = useState({
    headingHTML: headingHTML ?? "",
    headingHTMLEs: "",
    vimeoSrc: vimeoSrc ?? "",
    youtubeSrc: youtubeSrc ?? "",
    firestoreSrc: firestoreSrc ?? "",
  })

  const [isUploading, setIsUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [videoSource, setVideoSource] = useState(() => {
    if (firestoreSrc) return "upload"
    if (vimeoSrc) return "vimeo"
    if (youtubeSrc) return "youtube"
    return "youtube" // Default to youtube
  })

  const componentPayload = {
    componentId: "IV1",
    type: "INSTRUCTIONAL",
    headingHTML: componentData.headingHTML,
    headingHTMLEs: "",
    vimeoSrc: videoSource === "vimeo" ? componentData.vimeoSrc : "",
    youtubeSrc: videoSource === "youtube" ? componentData.youtubeSrc : "",
    firestoreSrc: videoSource === "upload" ? componentData.firestoreSrc : "",
    uuid: uuid || uuidv4(),
  }

  const handleInputChange = (e, field) => {
    setComponentData({
      ...componentData,
      [field]: e.target.value,
    })
  }

  const insertComponentAtLocation = () => {
    if (videoSource === "vimeo" && !componentData.vimeoSrc) {
      window.alert("Please provide a Vimeo URL")
      return
    }
    if (videoSource === "youtube" && !componentData.youtubeSrc) {
      window.alert("Please provide a YouTube URL")
      return
    }
    if (videoSource === "upload" && !componentData.firestoreSrc) {
      window.alert("Please upload a video file")
      return
    }

    isEditing ? updateComponent(componentPayload, coordinates) : addComponent(componentPayload)
    setIsEditing(false)
  }

  // Handle video upload
  const handleVideoUpload = async (file) => {
    if (!file) return

    // More flexible video file validation - check both MIME type and file extension
    const videoMimeTypes = [
      "video/mp4",
      "video/mov",
      "video/quicktime", // Added QuickTime MIME type
      "video/avi",
      "video/mkv",
      "video/webm",
      "video/x-msvideo", // Alternative AVI MIME type
      "video/3gpp", // 3GP videos
      "video/x-ms-wmv", // WMV videos
    ]

    const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm", ".3gp", ".wmv", ".m4v"]

    const fileName = file.name.toLowerCase()
    const hasValidExtension = videoExtensions.some((ext) => fileName.endsWith(ext))
    const hasValidMimeType = videoMimeTypes.includes(file.type)

    // Accept if either MIME type OR file extension is valid (some systems don't set MIME types correctly)
    if (!hasValidMimeType && !hasValidExtension) {
      window.alert("Please upload a valid video file (MP4, MOV, AVI, MKV, WEBM, 3GP, WMV, M4V)")
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    try {
      const fileId = componentPayload.uuid
      const folderName = "curriculum-hosted-files"

      // Upload the video using Firebase
      await firebase.uploadUserFileWithClear(folderName, fileId, file, null)

      // Get the video URL after upload
      const videoURL = await firebase.getUserFileURL(null, folderName, fileId)

      if (videoURL) {
        setComponentData((prev) => ({
          ...prev,
          firestoreSrc: videoURL,
        }))
      } else {
        throw new Error("No video URL returned after upload")
      }
    } catch (error) {
      console.error("Error uploading video:", error)
      window.alert(`Error uploading video: ${error.message || error}`)
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleVideoUpload(e.dataTransfer.files[0])
    }
  }

  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleVideoUpload(e.target.files[0])
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <label>
        Heading
        <HelpPopover title="Heading" content="Enter a heading for the video (optional)." />
      </label>
      <CurriculumQuill
        value={componentData.headingHTML}
        onChange={(newHeading) => setComponentData({ ...componentData, headingHTML: newHeading })}
        placeholder={"Enter heading here..."}
        style={{ marginBottom: "1rem" }}
      />

      <label>
        Video Source
        <HelpPopover title="Video Source" content="Choose whether to use Vimeo, YouTube, or upload a video file." />
      </label>
      <Form.Control
        as="select"
        className="custom-select-dropdown"
        value={videoSource}
        onChange={(e) => setVideoSource(e.target.value)}
        style={{ marginBottom: "1rem" }}
      >
        <option value="upload">Upload Video File</option>
        <option value="youtube">YouTube</option>
        <option value="vimeo">Vimeo</option>
      </Form.Control>

      {videoSource === "vimeo" && (
        <>
          <label>
            Vimeo URL
            <HelpPopover title="Vimeo URL" content="Paste a Vimeo video URL here." />
          </label>
          <input placeholder="Vimeo URL" value={componentData.vimeoSrc} onChange={(e) => handleInputChange(e, "vimeoSrc")} />
        </>
      )}

      {videoSource === "youtube" && (
        <>
          <label>
            YouTube URL
            <HelpPopover title="YouTube URL" content="Paste a YouTube video URL here." />
          </label>
          <input placeholder="YouTube URL" value={componentData.youtubeSrc} onChange={(e) => handleInputChange(e, "youtubeSrc")} />
        </>
      )}

      {videoSource === "upload" && (
        <>
          <label>
            Video Upload
            <HelpPopover title="Video Upload" content="Upload a video file from your computer or drag and drop a video into the upload area." />
          </label>

          {/* Video Upload Area */}
          <div
            className={`video-upload-area ${dragActive ? "drag-active" : ""}`}
            style={{
              border: `2px dashed ${dragActive ? "#40777c" : "#ccc"}`,
              borderRadius: "8px",
              padding: "40px 20px",
              textAlign: "center",
              backgroundColor: dragActive ? "#f0f8ff" : "#f9f9f9",
              cursor: "pointer",
              transition: "all 0.2s ease",
              marginBottom: "1rem",
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById("video-file-input").click()}
          >
            {isUploading ? (
              <div>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#40777c" />
                <p>Uploading video...</p>
                {uploadProgress > 0 && (
                  <div style={{ width: "200px", margin: "10px auto" }}>
                    <div
                      style={{
                        width: "100%",
                        backgroundColor: "#e0e0e0",
                        borderRadius: "10px",
                        height: "10px",
                      }}
                    >
                      <div
                        style={{
                          width: `${uploadProgress}%`,
                          backgroundColor: "#40777c",
                          height: "100%",
                          borderRadius: "10px",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <FontAwesomeIcon icon={faUpload} size="2x" color="#40777c" style={{ marginBottom: "10px" }} />
                <p style={{ margin: "10px 0", fontSize: "16px", fontWeight: "bold" }}>Drop video here or click to upload</p>
                <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>Supports MP4, MOV, AVI, MKV, WEBM</p>
                {componentData.firestoreSrc && (
                  <p style={{ margin: "10px 0", fontSize: "14px", color: "#40777c", fontWeight: "bold" }}>✓ Video uploaded successfully</p>
                )}
              </div>
            )}
          </div>

          <input
            id="video-file-input"
            type="file"
            onChange={handleFileInputChange}
            style={{ display: "none" }}
            disabled={isUploading}
            accept="video/*,.mov,.mp4,.avi,.mkv,.webm,.3gp,.wmv,.m4v"
          />
        </>
      )}

      <label>
        Preview
        <HelpPopover title="Preview" content="See how this video will appear to students." />
      </label>
      <div className="module-component" style={{ border: "1px solid #ccc", padding: "10px" }}>
        <VideoComponent data={componentData} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton variant="primary" size="mini" onClick={insertComponentAtLocation} style={{ marginTop: "1rem" }} disabled={isUploading}>
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}

/**
 * Renders the ImageComponentCreator, which allows users to create or edit an image component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @param {Function} props.handleTableCellSubmit - Function to handle table cell submission.
 * @returns {JSX.Element} The image component creator.
 */
const ImageComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing, isTableComponent, handleTableCellSubmit }) => {
  const { addComponent, updateComponent } = useEditorActions()
  const { src, headingHTML } = initialData || {}
  const [componentData, setComponentData] = useState({
    src: src ?? "",
    headingHTML: headingHTML ?? "",
  })

  const componentPayload = {
    componentId: "II1",
    type: "INSTRUCTIONAL",
    headingHTML: componentData.headingHTML,
    headingHTMLEs: "",
    src: componentData.src,
  }

  const insertComponentAtLocation = async () => {
    if (!componentPayload.src) {
      window.alert("Please upload an image")
      return
    }
    isEditing ? updateComponent(componentPayload, coordinates) : addComponent(componentPayload)
    setIsEditing(false)
  }

  return (
    <div className="image-component-creator">
      <label>
        Image Heading
        <HelpPopover title="Image Heading" content="Enter a heading for the image (optional). This will appear above the image." />
      </label>
      <CurriculumQuill
        value={componentPayload.headingHTML}
        onChange={(newHeading) => setComponentData({ ...componentData, headingHTML: newHeading })}
        placeholder={"Enter heading here..."}
        style={{ marginBottom: "1rem" }}
      />
      <label>
        Image Preview
        <HelpPopover title="Image Preview" content="Upload an image to display below." />
      </label>
      <CurriculumImageUploadSection
        imageURL={componentPayload.src}
        updateImageURL={(newURL) => setComponentData({ ...componentData, src: newURL })}
        imageClassName="image-component-preview"
      />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={isTableComponent ? () => handleTableCellSubmit(componentPayload) : () => insertComponentAtLocation()}
          style={{ marginTop: "1rem" }}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}

// add the following to the animationLibraryMap, always end in _1 (flower,graph,love,magic,record,speaker,success,sun,wow)

const animationLibraryMap = {
  ["success_1"]: "/assets/animations/lottie/success_1.lottie",
  ["bird_1"]: "/assets/animations/lottie/bird_1.lottie",
  ["congrats_1"]: "/assets/animations/lottie/congrats_1.lottie",
  ["money_1"]: "/assets/animations/lottie/money_1.lottie",
  ["chat_1"]: "/assets/animations/lottie/chat_1.lottie",
  ["confetti_1"]: "/assets/animations/lottie/confetti_1.lottie",
  ["firework_1"]: "/assets/animations/lottie/firework_1.lottie",
  ["flower_1"]: "/assets/animations/lottie/flower_1.lottie",
  ["graph_1"]: "/assets/animations/lottie/graph_1.lottie",
  ["love_1"]: "/assets/animations/lottie/love_1.lottie",
  ["magic_1"]: "/assets/animations/lottie/magic_1.lottie",
  ["record_1"]: "/assets/animations/lottie/record_1.lottie",
  ["speaker_1"]: "/assets/animations/lottie/speaker_1.lottie",
  ["sun_1"]: "/assets/animations/lottie/sun_1.lottie",
  ["wow_1"]: "/assets/animations/lottie/wow_1.lottie",
  ["check_circle_1"]: "/assets/animations/lottie/check_circle_1.lottie",
  ["check_dot_1"]: "/assets/animations/lottie/check_dot_1.lottie",
}

const completionAnimationLibraryMap = {
  ["success_1"]: "/assets/animations/lottie/success_1.lottie",
  ["congrats_1"]: "/assets/animations/lottie/congrats_1.lottie",
  ["firework_1"]: "/assets/animations/lottie/firework_1.lottie",
  ["confetti_1"]: "/assets/animations/lottie/confetti_1.lottie",
  ["check_circle_1"]: "/assets/animations/lottie/check_circle_1.lottie",
  ["check_dot_1"]: "/assets/animations/lottie/check_dot_1.lottie",
}

const AnimationComponentPreviewCard = ({ isSelected, animationKey, handleSelectAnimation }) => {
  return (
    <div
      key={`${animationKey}-${isSelected}`}
      className="animation-library-item"
      style={{
        background: isSelected ? "#d4fb44" : "#f7f7f7", // highlight if selected
        borderRadius: "8px",
        padding: "6px",
        cursor: "pointer",
        textAlign: "center",
        transition: "box-shadow 0.2s",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
      onClick={() => handleSelectAnimation(animationKey)}
    >
      <div
        className="animation-library-preview"
        style={{
          width: "100px",
          height: "100px",
          margin: "0 auto 6px auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <DotLottieReact src={animationLibraryMap[animationKey]} autoplay loop={isSelected} />
      </div>
      <div className="animation-library-name" style={{ fontSize: "0.8rem", color: "#40777c", wordBreak: "break-all" }}>
        {animationKey}
      </div>
    </div>
  )
}

/**
 * Renders the AnimationComponentCreator, which allows users to create or edit an image component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @param {Function} props.handleTableCellSubmit - Function to handle table cell submission.
 * @returns {JSX.Element} The image component creator.
 */
const AnimationComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing, isTableComponent, handleTableCellSubmit }) => {
  const { addComponent, updateComponent } = useEditorActions()
  const { src, headingHTML, fileName, loop, speed } = initialData || {}
  const [componentData, setComponentData] = useState({
    src: src ?? "",
    fileName: fileName ?? "",
    headingHTML: headingHTML ?? "",
    loop: loop ?? true,
    speed: speed ?? 1,
  })

  const componentPayload = {
    componentId: "IA1",
    type: "INSTRUCTIONAL",
    headingHTML: componentData.headingHTML,
    fileName: componentData.fileName,
    headingHTMLEs: "",
    loop: componentData.loop,
    speed: componentData.speed,
  }

  const insertComponentAtLocation = async () => {
    isEditing ? updateComponent(componentPayload, coordinates) : addComponent(componentPayload)
    setIsEditing(false)
  }

  const handleSelectAnimation = (animationKey) => {
    setComponentData({ ...componentData, fileName: animationKey })
  }

  return (
    <div className="animation-component-creator">
      <label>
        Animation Heading
        <HelpPopover title="Animation Heading" content="Enter a heading for the animation (optional). This will appear above the animation." />
      </label>
      <CurriculumQuill
        value={componentPayload.headingHTML}
        onChange={(newHeading) => setComponentData({ ...componentData, headingHTML: newHeading })}
        placeholder={"Enter heading here..."}
        style={{ marginBottom: "1rem" }}
        configType="COMPLETION_HEADING"
      />
      <label>
        Animation Library
        <HelpPopover title="Animation Library" content="Select an animation from the library below." />
      </label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
          gap: "12px",
          margin: "12px 0",
        }}
      >
        {Object.keys(animationLibraryMap).map((animationKey) => (
          <AnimationComponentPreviewCard
            key={animationKey}
            animationKey={animationKey}
            isSelected={componentData.fileName === animationKey}
            handleSelectAnimation={handleSelectAnimation}
          />
        ))}
      </div>
      <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1rem" }}>
        <label>
          Loop
          <HelpPopover title="Loop" content="Toggle whether the animation should loop continuously." />
          <input
            type="checkbox"
            checked={componentData.loop}
            onChange={(e) => setComponentData({ ...componentData, loop: e.target.checked })}
            style={{ marginLeft: "0.5rem" }}
            className="module-component-checkbox"
          />
        </label>
        <label>
          Speed
          <HelpPopover title="Speed" content="Set the playback speed for the animation." />
          <Form.Control
            as="select"
            value={componentData.speed}
            onChange={(e) => setComponentData({ ...componentData, speed: Number(e.target.value) })}
            style={{ marginLeft: "0.5rem", width: "120px", display: "inline-block" }}
            className="custom-select-dropdown"
          >
            <option value={0.25}>0.25x</option>
            <option value={0.5}>0.5x</option>
            <option value={1}>1x</option>
            <option value={1.5}>1.5x</option>
            <option value={2}>2x</option>
          </Form.Control>
        </label>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={isTableComponent ? () => handleTableCellSubmit(componentPayload) : () => insertComponentAtLocation()}
          style={{ marginTop: "1rem" }}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}

/**
 * Renders the WingspansStoryComponentCreator, which allows users to create or edit a Wingspans story component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @returns {JSX.Element} The Wingspans story component creator.
 */
const WingspansStoryComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing }) => {
  const { quote, bodyHTML, link, imageSrc } = initialData || {}
  const { addComponent, updateComponent } = useEditorActions()
  const [componentData, setComponentData] = useState({
    quote: quote ?? "",
    quoteEs: "",
    bodyHTML: bodyHTML ?? "",
    quoteAuthor: "",
    bodyHTMLEs: "",
    link: link ?? "",
    imageSrc: imageSrc ?? "",
  })

  const allStories = useStories("en-US") // Assuming locale is "en-US"

  const [searchTerm, setSearchTerm] = useState("")
  const [filteredStories, setFilteredStories] = useState([])

  const handleInputChange = (e, field) => {
    setComponentData({
      ...componentData,
      [field]: e.target.value,
    })
  }

  const handleSearchChange = (e) => {
    const term = e.target.value
    setSearchTerm(term)
    if (term.length > 0) {
      const filtered = allStories.filter((story) => story.title.toLowerCase().includes(term.toLowerCase()))
      setFilteredStories(filtered)
    } else {
      setFilteredStories([])
    }
  }

  const handleStorySelect = (story) => {
    setComponentData({
      quote: story.story_quote,
      link: `/story/${story.slug}`,
      imageSrc: story.story_personal_photo,
    })
    setSearchTerm(story.title)
    setFilteredStories([])
  }

  const insertComponentAtLocation = () => {
    const componentPayload = {
      componentId: "IW1",
      type: "INSTRUCTIONAL",
      quote: componentData.quote,
      quoteEs: "",
      bodyHTML: componentData.bodyHTML,
      bodyHTMLEs: "",
      link: componentData.link,
      imageSrc: componentData.imageSrc,
    }

    isEditing ? updateComponent(componentPayload, coordinates) : addComponent(componentPayload)
    setIsEditing(false)
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
      }}
    >
      <label>
        Search Stories
        <HelpPopover title="Search Stories" content="Search for a Wingspans story to use as a template for this component." />
      </label>
      <input placeholder="Search Stories" value={searchTerm} onChange={handleSearchChange} />
      {filteredStories.length > 0 && (
        <ul style={{ listStyleType: "none", padding: 0, maxHeight: "150px", overflowY: "auto", border: "1px solid #ccc" }}>
          {filteredStories.map((story) => (
            <li key={story.id} style={{ cursor: "pointer" }} onClick={() => handleStorySelect(story)}>
              {story.title}
            </li>
          ))}
        </ul>
      )}

      <label>
        Quote
        <HelpPopover title="Quote" content="Add an inspirational or relevant quote for the story." />
      </label>
      <input placeholder="Quote" value={componentData.quote} onChange={(e) => handleInputChange(e, "quote")} />

      <label>
        Text
        <HelpPopover title="Text" content="Enter the main text or body for the story." />
      </label>
      <input placeholder="Text" value={componentData.bodyHTML} onChange={(e) => handleInputChange(e, "bodyHTML")} />

      <label>
        Link
        <HelpPopover title="Link" content="Provide a link to the full story, this should fill in automatically when the story is selected" />
      </label>
      <input placeholder="Link" value={componentData.link} onChange={(e) => handleInputChange(e, "link")} />

      <label>
        Image Source
        <HelpPopover title="Image Source" content="Paste the URL for the image to display with this story." />
      </label>
      <input placeholder="Image Source" value={componentData.imageSrc} onChange={(e) => handleInputChange(e, "imageSrc")} />

      <label>
        Preview
        <HelpPopover title="Preview" content="See how this Wingspans story will appear to students." />
      </label>
      <div className="module-component" style={{ border: "1px solid #ccc", padding: "10px" }}>
        <WingspansStoryComponent data={componentData} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={insertComponentAtLocation}
          style={{ marginTop: "1rem" }}
          // disabled={!componentData.quote || !componentData.bodyHTML || !componentData.link || !componentData.imageSrc}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}

const WingspansFeatureComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing }) => {
  const { addComponent, updateComponent } = useEditorActions()
  const { label, labelEs, href } = initialData || {}

  const [componentData, setComponentData] = useState({
    label: label ?? "Interest Profiler",
    href: href ?? "/assessment/personality",
  })

  const predefinedOptions = [
    { label: "Interest Profiler", value: "/assessment/personality" },
    { label: "TAL-E", value: "/tal-e" },
    { label: "Explore", value: "/dashboard/?tab=explore" },
    { label: "Resume", value: "/dashboard/?tab=portfolio" },
    { label: "Career Plans", value: "/dashboard/?tab=career-plans" },
    { label: "Skills Builder", value: "/dashboard/?tab=work-readiness" },
    { label: "Custom", value: "custom" },
  ]

  const [isCustomHref, setIsCustomHref] = useState(() => {
    const predefinedValues = predefinedOptions.map((option) => option.value)
    return !!href && !predefinedValues.includes(href)
  })

  const handleInputChange = (e, field) => {
    setComponentData((previousComponentData) => ({
      ...previousComponentData,
      [field]: e.target.value,
    }))
  }

  const handleHrefChange = (e) => {
    const selectedValue = e.target.value
    if (selectedValue === "custom") {
      setIsCustomHref(true)
      setComponentData((previousComponentData) => ({ ...previousComponentData, href: "", label: "" })) // Clear href for custom input
    } else {
      setIsCustomHref(false)
      setComponentData((previousComponentData) => ({
        ...previousComponentData,
        href: selectedValue,
        label: predefinedOptions.find((option) => option.value === selectedValue)?.label ?? "",
      }))
    }
  }

  const insertComponentAtLocation = () => {
    const componentPayload = {
      componentId: "IW2",
      type: "INSTRUCTIONAL",
      label: componentData.label,
      labelEs: "",
      href: componentData.href,
    }

    isEditing ? updateComponent(componentPayload, coordinates) : addComponent(componentPayload)
    setIsEditing(false)
  }

  return (
    <div>
      <label>
        Link
        <HelpPopover title="Link" content="Select a predefined link or choose Custom to enter your own URL." />
      </label>
      <Form.Control
        as="select"
        className="custom-select-dropdown"
        value={isCustomHref ? "custom" : componentData.href}
        onChange={handleHrefChange}
      >
        {predefinedOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Control>

      {isCustomHref && (
        <>
          <label className="w-mt-1">
            Custom Link
            <HelpPopover
              title="Custom Link"
              content="Enter a custom URL for the button to go to any part of the site, please use a relative link such as /career/pharmacy-technician/ "
            />
          </label>
          <input placeholder="" value={componentData.href} onChange={(e) => handleInputChange(e, "href")} />
        </>
      )}

      <label className="w-mt-1">
        Button Label
        <HelpPopover title="Button Label" content="Enter the text that will appear on the button." />
      </label>
      <input value={componentData.label} onChange={(e) => handleInputChange(e, "label")} />

      <label className="w-mt-1">
        Preview
        <HelpPopover title="Preview" content="See how this Wingspans feature button will appear to students." />
      </label>
      <div className="module-component" style={{ border: "1px solid #ccc", padding: "10px" }}>
        <WingspansFeatureComponent data={componentData} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton variant="primary" size="mini" onClick={insertComponentAtLocation} style={{ marginTop: "1rem" }}>
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}

/**
 * Renders the DropdownComponentCreator, which allows users to create or edit a dropdown question component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @param {Function} props.handleTableCellSubmit - Function to handle table cell submission.
 * @returns {JSX.Element} The dropdown component creator.
 */
const DropdownComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing, isTableComponent, handleTableCellSubmit }) => {
  const { title, question, options } = initialData || {}

  const [componentData, setComponentData] = useState({
    componentId: "QD1",
    type: "QUESTION",
    title: title ?? "",
    titleEs: "",
    question: question ?? "",
    questionEs: "",
    options: options ? options.map((option) => ({ ...option })) : [{ option: "", optionEs: "", isCorrect: null }],
    userResponse: null,
  })

  const { addComponent, updateComponent } = useEditorActions()

  const handleOptionChange = (e, index) => {
    const newOptions = [...componentData.options]
    newOptions[index] = { ...newOptions[index], option: e.target.value }
    setComponentData({
      ...componentData,
      options: newOptions,
    })
  }

  const addOption = () => {
    setComponentData({
      ...componentData,
      options: [...componentData.options, { option: "", optionEs: "", isCorrect: null }],
    })
  }

  const removeOption = (index) => {
    const newOptions = componentData.options.filter((_, i) => i !== index)
    setComponentData({
      ...componentData,
      options: newOptions,
    })
  }

  const insertComponentAtLocation = () => {
    isEditing ? updateComponent(componentData, coordinates) : addComponent(componentData)
    setIsEditing(false)
  }

  const handleRichTextChange = (value) => {
    setComponentData({ ...componentData, question: value })
  }

  const handleCorrectAnswerChange = (index) => {
    const newOptions = componentData.options.map((option, i) => ({
      ...option,
      isCorrect: i === index ? true : null,
    }))
    setComponentData({
      ...componentData,
      options: newOptions,
    })
  }

  return (
    <div>
      <label>
        Question
        <HelpPopover title="Question" content="Enter the dropdown question prompt." />
      </label>
      <CurriculumQuill
        value={componentData.question}
        onChange={handleRichTextChange}
        placeholder={"Enter question here..."}
        style={{ marginBottom: "1rem" }}
      />

      <div>
        <label>
          Options
          <HelpPopover title="Options" content="Add dropdown answer choices. Only one can be correct." />
        </label>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: ".5rem" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>
                Option
                <HelpPopover title="Option" content="Enter the text for each dropdown answer choice." />
              </th>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>
                Correct
                <HelpPopover title="Correct" content="Select which answer is correct." />
              </th>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>
                Actions
                <HelpPopover title="Actions" content="Remove an option from the list." />
              </th>
            </tr>
          </thead>
          <tbody>
            {componentData.options.map((option, index) => (
              <tr key={index}>
                <td style={{ border: "1px solid #ccc", padding: "10px" }}>
                  <input
                    placeholder={`Option ${index + 1}`}
                    value={option.option}
                    onChange={(e) => handleOptionChange(e, index)}
                    style={{ width: "100%" }}
                  />
                </td>
                <td style={{ border: "1px solid #ccc", padding: "10px", textAlign: "center" }}>
                  <input
                    type="radio"
                    name="correctAnswer"
                    value={option.option}
                    checked={option.isCorrect === true}
                    onChange={() => handleCorrectAnswerChange(index)}
                  />
                </td>
                <td style={{ border: "1px solid #ccc", padding: "10px", textAlign: "center" }}>
                  <WingsButton variant="tertiary" size="mini" onClick={() => removeOption(index)}>
                    Remove
                  </WingsButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <WingsButton variant="tertiary" size="mini" onClick={addOption} style={{ marginBottom: "1rem" }}>
          + Add Option
        </WingsButton>
      </div>

      {!isTableComponent && (
        <>
          <label>
            Preview
            <HelpPopover title="Preview" content="See how this dropdown question will appear to students." />
          </label>
          <div className="module-component" style={{ border: "1px solid #ccc", padding: "10px" }}>
            <DropdownComponent data={componentData} />
          </div>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={isTableComponent ? () => handleTableCellSubmit(componentData) : () => insertComponentAtLocation()}
          style={{ marginTop: "1rem" }}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}
/**
 * Renders the MultipleChoiceComponentCreator, which allows users to create or edit a multiple-choice question component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @param {Function} props.handleTableCellSubmit - Function to handle table cell submission.
 * @returns {JSX.Element} The multiple-choice component creator.
 */
const MultipleChoiceComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing, isTableComponent, handleTableCellSubmit }) => {
  const { title, question, options, correctAnswer } = initialData || {}

  const [componentData, setComponentData] = useState({
    componentId: "QM1",
    type: "QUESTION",
    title: title ?? "",
    titleEs: "",
    question: question ?? "",
    questionEs: "",
    options: options ? options.map((option) => ({ ...option })) : [{ option: "", optionEs: "", isCorrect: false }],
    correctAnswer: correctAnswer ?? "",
    userResponse: null,
    displayRow: false,
    isGradingDisabled: false,
  })

  const { addComponent, updateComponent } = useEditorActions()

  const handleOptionChange = (e, index) => {
    const newOptions = [...componentData.options]
    newOptions[index] = { ...newOptions[index], option: e.target.value }
    setComponentData({
      ...componentData,
      options: newOptions,
    })
  }

  const addOption = () => {
    setComponentData({
      ...componentData,
      options: [...componentData.options, { option: "", optionEs: "", isCorrect: false }],
    })
  }

  const removeOption = (index) => {
    const newOptions = componentData.options.filter((_, i) => i !== index)
    setComponentData({
      ...componentData,
      options: newOptions,
      correctAnswer: componentData.correctAnswer === componentData.options[index].option ? "" : componentData.correctAnswer,
    })
  }

  const insertComponentAtLocation = () => {
    isEditing ? updateComponent(componentData, coordinates) : addComponent(componentData)
    setIsEditing(false)
  }

  const handleRichTextChange = (value) => {
    setComponentData({ ...componentData, question: value })
  }

  const handleCorrectAnswerChange = (index) => {
    const newOptions = componentData.options.map((option, i) => ({
      ...option,
      isCorrect: i === index,
    }))
    setComponentData({
      ...componentData,
      options: newOptions,
      correctAnswer: newOptions[index].option,
    })
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <label>
        Question
        <HelpPopover title="Question" content="Enter the question prompt for this multiple choice question." />
      </label>
      {/* <ReactQuill theme="snow" value={componentData.question} onChange={handleRichTextChange} placeholder="Enter question here..." /> */}
      <CurriculumQuill
        value={componentData.question}
        onChange={handleRichTextChange}
        placeholder={"Enter question here..."}
        style={{ marginBottom: "1rem" }}
      />
      <div>
        <label>
          Options
          <HelpPopover title="Options" content="Add answer choices for this question. Mark the correct answer with the radio button." />
        </label>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>
                Option
                <HelpPopover title="Option" content="Enter the text for each answer choice." />
              </th>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>
                Correct
                <HelpPopover title="Correct" content="Select which answer is correct." />
              </th>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>
                Actions
                <HelpPopover title="Actions" content="Remove an option from the list." />
              </th>
            </tr>
          </thead>
          <tbody>
            {componentData.options.map((option, index) => (
              <tr key={index}>
                <td style={{ border: "1px solid #ccc", padding: "10px" }}>
                  <input
                    placeholder={`Option ${index + 1}`}
                    value={option.option}
                    onChange={(e) => handleOptionChange(e, index)}
                    style={{ width: "100%" }}
                  />
                </td>
                <td style={{ border: "1px solid #ccc", padding: "10px", textAlign: "center" }}>
                  <input
                    type="radio"
                    name="correctAnswer"
                    value={option.option}
                    checked={option.isCorrect}
                    onChange={() => handleCorrectAnswerChange(index)}
                  />
                </td>
                <td style={{ border: "1px solid #ccc", padding: "10px", textAlign: "center" }}>
                  <WingsButton variant="tertiary" size="mini" onClick={() => removeOption(index)}>
                    Remove
                  </WingsButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <WingsButton variant="tertiary" size="mini" onClick={addOption}>
            + Add Option
          </WingsButton>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            {" "}
            <label style={{ marginBottom: "0" }}>Disable Grading</label>
            <input
              type="checkbox"
              checked={componentData.isGradingDisabled}
              onChange={() => setComponentData({ ...componentData, isGradingDisabled: !componentData.isGradingDisabled })}
            ></input>
          </div>
        </div>
      </div>

      {!isTableComponent && (
        <>
          <label>Preview</label>
          <div className="module-component" style={{ border: "1px solid #ccc", padding: "10px" }}>
            <MultipleChoiceComponent data={componentData} />
          </div>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={isTableComponent ? () => handleTableCellSubmit(componentData) : () => insertComponentAtLocation()}
          style={{ marginTop: "1rem" }}
          // disabled={!componentData.question || componentData.options.length < 2 || !componentData.correctAnswer}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}

/**
 * Renders the OpenResponseComponentCreator, which allows users to create or edit an open-response question component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @param {Function} props.handleTableCellSubmit - Function to handle table cell submission.
 * @returns {JSX.Element} The open-response component creator.
 */
const OpenResponseComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing, isTableComponent, handleTableCellSubmit }) => {
  const { addComponent, updateComponent } = useEditorActions()
  const [question, setQuestion] = useState(initialData?.question ?? "")
  const [wordMin, setWordMin] = useState(initialData?.responseMinimum || 1) // Default to 1 if not set

  const componentPayload = {
    componentId: "QO1",
    type: "QUESTION",
    question: question,
    questionEs: "",
    userResponse: null,
    responseMinimum: wordMin ?? 1,
    placeholder: null,
  }

  const insertComponentAtLocation = () => {
    isEditing ? updateComponent(componentPayload, coordinates) : addComponent(componentPayload)
    setIsEditing(false)
  }

  return (
    <div>
      <label>
        Prompt
        <HelpPopover title="Prompt" content="Enter the open response prompt for students to answer." />
      </label>
      <CurriculumQuill value={question} onChange={setQuestion} placeholder={"Enter question here..."} style={{ marginBottom: "1rem" }} />

      <label>
        Word Minimum
        <HelpPopover title="Word Minimum" content="Set the minimum number of words required for a valid response." />
      </label>
      <input
        type="number"
        min="1"
        value={wordMin}
        onChange={(e) => {
          setWordMin(Math.max(1, parseInt(e.target.value, 10)))
        }}
        style={{ width: "100px", marginBottom: "1rem" }}
      />

      {!isTableComponent && (
        <>
          <label>
            Preview
            <HelpPopover title="Preview" content="See how this open response question will appear to students." />
          </label>
          <div style={{ border: "1px solid #ccc", padding: "10px" }}>
            <OpenResponseComponent data={{ question }} />
          </div>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={isTableComponent ? () => handleTableCellSubmit(componentPayload) : () => insertComponentAtLocation()}
          style={{ marginTop: "1rem" }}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}
export const CheckboxComponent = ({ data, coordinates = {}, cellCoordinates = {}, isTableComponent }) => {
  const { dispatch, moduleUI } = useModuleContext()
  const { mode } = moduleUI
  const { question, questionEs, options, correctAnswers, userResponse = [], isGradingDisabled } = data
  const { slideIndex, componentIndex, sectionIndex } = coordinates
  const { locale } = useIntl()

  const handleCheckboxChange = (index) => {
    const newResponse = userResponse?.includes(index) ? userResponse.filter((r) => r !== index) : [...userResponse, index]

    dispatch({
      type: "RECORD_RESPONSE",
      slideIndex,
      componentIndex,
      sectionIndex,
      isTableComponent: isTableComponent,
      cellCoordinates: cellCoordinates,
      payload: newResponse,
    })
  }

  const renderSubmissionStyle = (index) => {
    const isUserResponse = userResponse.includes(index)
    const isCorrectAnswer = options[index].isCorrect === true
    const isSubmissionMode = mode === "SUBMISSION"

    if (isSubmissionMode) {
      // User-selected correct answer
      if (isUserResponse && isCorrectAnswer) {
        return { backgroundColor: "#d4fb44", fontWeight: "bold" }
      }
      // Always highlight correct answers with a green border
      if (isCorrectAnswer) {
        return { border: "2px solid #40777c", fontWeight: "bold", marginBottom: "1px", borderRadius: "3px" }
      }
      // User-selected incorrect answers should be crossed out
      if (isUserResponse && !isCorrectAnswer) {
        return { textDecoration: "line-through", textDecorationColor: "#94342a" }
      }
    } else {
      // User-selected correct answer
      if (isUserResponse && isCorrectAnswer) {
        return { backgroundColor: "#d4fb44", fontWeight: "bold" }
      }
      // User-selected incorrect answer
      if (isUserResponse && !isCorrectAnswer && !isGradingDisabled) {
        return { textDecoration: "line-through", textDecorationColor: "#94342a" }
      }
    }

    return { fontWeight: "400", border: "1px solid transparent" }
  }

  const getDisabilityStatus = (index) => {
    if ((userResponse?.includes(index) && !isGradingDisabled) || mode !== "STUDENT") {
      return true
    } else {
      return false
    }
  }

  const cleanLocalizedQuestionHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, questionEs, question))

  return (
    <>
      <div className="module-component-checkbox">
        {hasHTMLContent(cleanLocalizedQuestionHTML) && (
          <div dangerouslySetInnerHTML={{ __html: cleanLocalizedQuestionHTML }} className="module-component-question" />
        )}

        {options.map((choice, index) => {
          const { option, optionEs } = choice

          const displayOption = getLocalizedTextWithFallback(locale, optionEs, option)
          return (
            <div style={{ ...renderSubmissionStyle(index) }} key={`checkbox-option-${index}`}>
              <span style={{ display: "flex", alignItems: "center", gap: "1rem", paddingLeft: ".25rem" }}>
                <input
                  // disabled={getDisabilityStatus(index)}
                  style={{ pointerEvents: getDisabilityStatus(index) ? "none" : "auto" }}
                  type="checkbox"
                  value={displayOption}
                  checked={userResponse?.includes(index)}
                  onChange={() => handleCheckboxChange(index)}
                />
                {displayOption}
              </span>
            </div>
          )
        })}
      </div>
    </>
  )
}

export const TableComponent = ({ data, coordinates = {}, isEditingCell, handleCellSelect, handleHeaderSelect }) => {
  const { colHeadings = [], rows, rowHeadings = [], headingHTML, headingHTMLEs, isBorderless = true, isRowHeadEnabled, isColHeadEnabled } = data || {}
  const { locale } = useIntl()
  const tableScrollRef = useRef(null)
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false)
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false)

  const cleanLocalizedHeadingHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, headingHTMLEs, headingHTML))

  useEffect(() => {
    const tableScrollElement = tableScrollRef.current
    if (!tableScrollElement) return

    const updateScrollState = () => {
      const hasOverflow = tableScrollElement.scrollWidth - tableScrollElement.clientWidth > 1
      const maxScrollLeft = Math.max(tableScrollElement.scrollWidth - tableScrollElement.clientWidth, 0)
      const atEnd = hasOverflow && tableScrollElement.scrollLeft >= maxScrollLeft - 1

      setHasHorizontalOverflow(hasOverflow)
      setIsScrolledToEnd(atEnd)
    }

    updateScrollState()
    tableScrollElement.addEventListener("scroll", updateScrollState, { passive: true })

    let resizeObserver
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateScrollState)
      resizeObserver.observe(tableScrollElement)

      const tableElement = tableScrollElement.querySelector("table")
      if (tableElement) {
        resizeObserver.observe(tableElement)
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateScrollState)
    }

    return () => {
      tableScrollElement.removeEventListener("scroll", updateScrollState)

      if (resizeObserver) {
        resizeObserver.disconnect()
      }

      if (typeof window !== "undefined") {
        window.removeEventListener("resize", updateScrollState)
      }
    }
  }, [colHeadings, isBorderless, isColHeadEnabled, isEditingCell, isRowHeadEnabled, locale, rowHeadings, rows])

  const handleScrollToggle = () => {
    const tableScrollElement = tableScrollRef.current
    if (!tableScrollElement) return

    const maxScrollLeft = Math.max(tableScrollElement.scrollWidth - tableScrollElement.clientWidth, 0)
    const nextScrollLeft = isScrolledToEnd ? 0 : maxScrollLeft

    tableScrollElement.scrollTo({ left: nextScrollLeft, behavior: "smooth" })
  }

  return (
    <div className={`module-component-table ${isEditingCell ? "editing" : ""}`}>
      {hasHTMLContent(cleanLocalizedHeadingHTML) && (
        <div dangerouslySetInnerHTML={{ __html: cleanLocalizedHeadingHTML }} className="module-component-question" />
      )}

      {hasHorizontalOverflow && (
        <div className="module-component-table-scroll-hint">
          <button type="button" className="module-component-table-scroll-hint-icon" onClick={handleScrollToggle}>
            {isScrolledToEnd ? "↤ Scroll" : "↦ Scroll"}
          </button>
        </div>
      )}

      <div className="module-component-table-scroll" ref={tableScrollRef}>
        <table className={isBorderless ? "table-borderless" : ""}>
          <thead>
            <tr>
              {isColHeadEnabled && isRowHeadEnabled && <th className="table-cell table-row-heading"></th>}
              {isColHeadEnabled &&
                colHeadings?.map((heading, index) => (
                  <th key={index} className="table-cell">
                    {isEditingCell && (
                      <div className="cell-overlay" onClick={() => handleHeaderSelect(heading, "col", index)}>
                        <span className="edit-icon">
                          <FontAwesomeIcon icon={faEdit} size="lg" color="#40777c" />
                        </span>
                      </div>
                    )}
                    {heading}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {rows?.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`} className={isBorderless ? "table-borderless" : ""}>
                {isRowHeadEnabled && (
                  <th className="table-cell table-row-heading">
                    {isEditingCell && (
                      <div className="cell-overlay" onClick={() => handleHeaderSelect(rowHeadings[rowIndex], "row", rowIndex)}>
                        <span className="edit-icon">
                          <FontAwesomeIcon icon={faEdit} size="lg" color="#40777c" />
                        </span>
                      </div>
                    )}
                    {rowHeadings[rowIndex] ?? ""}
                  </th>
                )}
                {row?.map((component, colIndex) => (
                  <td key={`col-${rowIndex}-${colIndex}`} className={isBorderless ? "table-cell table-borderless" : "table-cell"}>
                    {isEditingCell && (
                      <div className="cell-overlay" onClick={() => handleCellSelect(component, rowIndex, colIndex)}>
                        <span className="edit-icon">
                          <FontAwesomeIcon icon={faEdit} size="lg" color="#40777c" />
                        </span>
                      </div>
                    )}
                    <TableModuleComponent
                      data={component}
                      coordinates={coordinates}
                      cellCoordinates={{
                        rowIndex: rowIndex,
                        colIndex: colIndex,
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="module-component-table-scroll-spacer" aria-hidden="true" />
      </div>
    </div>
  )
}

/**
 * Renders a module component inside a table cell.
 * @param {Object} props.data - Data for the component.
 * @param {Object} props.coordinates - Coordinates of the table in the module.
 * @param {Object} props.cellCoordinates - Coordinates of the cell in the table.
 * @returns {JSX.Element} The table module component.
 */
export const TableModuleComponent = ({ data, coordinates = {}, cellCoordinates = {} }) => {
  // this could use children instead, will need generic overlay to render hover options
  const { moduleUI } = useModuleContext()
  const { mode } = moduleUI
  // will have variable keys depending on type of component
  const { componentId } = data

  const [isHovered, setIsHovered] = useState(false)

  const renderComponent = () => {
    switch (componentId) {
      case "QM1":
        return <MultipleChoiceComponent data={data} coordinates={coordinates} cellCoordinates={cellCoordinates} isTableComponent={true} />
      case "IR1":
        // dont display in the submission view
        return <RichTextComponent data={data} coordinates={coordinates} cellCoordinates={cellCoordinates} isTableComponent={true} />
      case "QO1":
        return <OpenResponseComponent data={data} coordinates={coordinates} cellCoordinates={cellCoordinates} isTableComponent={true} />
      case "QC1":
        return <CheckboxComponent data={data} coordinates={coordinates} cellCoordinates={cellCoordinates} isTableComponent={true} />
      case "QD1":
        return <DropdownComponent data={data} coordinates={coordinates} cellCoordinates={cellCoordinates} isTableComponent={true} />
      case "IV1":
        return <VideoComponent data={data} coordinates={coordinates} cellCoordinates={cellCoordinates} isTableComponent={true} />
      case "II1":
        return <ImageComponent data={data} coordinates={coordinates} cellCoordinates={cellCoordinates} isTableComponent={true} />
      case "IF1":
        return <FileDownloadComponent data={data} coordinates={coordinates} cellCoordinates={cellCoordinates} isTableComponent={true} />
      case "IF2":
        return <FileUploadComponent data={data} coordinates={coordinates} cellCoordinates={cellCoordinates} isTableComponent={true} />
      default:
        return null
    }
  }

  return (
    <>
      <div className="module-component" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        {renderComponent()}

        <div
          className="module-component-hover-options"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            background: "white",
            display: isHovered && mode === "EDITOR" ? "flex" : "none",
            height: "100%",
            flexDirection: "column",
          }}
        >
          <ModuleComponentEditModal coordinates={coordinates} />
          {/* <button onClick={handleDelete}>Delete</button> */}
        </div>
      </div>
    </>
  )
}

/**
 * Renders the CheckboxComponentCreator, which allows users to create or edit a checkbox question component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @param {Function} props.handleTableCellSubmit - Function to handle table cell submission.
 * @returns {JSX.Element} The checkbox component creator.
 */
const CheckboxComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing, isTableComponent, handleTableCellSubmit }) => {
  const { title, question, options, isGradingDisabled } = initialData || {}

  const [componentData, setComponentData] = useState({
    componentId: "QC1",
    type: "QUESTION",
    title: title ?? "",
    titleEs: "",
    question: question ?? "",
    questionEs: "",
    options: options ? options.map((option) => ({ ...option })) : [{ option: "", optionEs: "", isCorrect: false }],
    userResponse: [],
    isGradingDisabled: isGradingDisabled ?? false,
  })

  const { addComponent, updateComponent } = useEditorActions()

  const handleOptionChange = (e, index) => {
    const newOptions = [...componentData.options]
    newOptions[index] = { ...newOptions[index], option: e.target.value }
    setComponentData({
      ...componentData,
      options: newOptions,
    })
  }

  const addOption = () => {
    setComponentData({
      ...componentData,
      options: [...componentData.options, { option: "", optionEs: "", isCorrect: false }],
    })
  }

  const removeOption = (index) => {
    const newOptions = componentData.options.filter((_, i) => i !== index)
    setComponentData({
      ...componentData,
      options: newOptions,
    })
  }

  const insertComponentAtLocation = () => {
    isEditing ? updateComponent(componentData, coordinates) : addComponent(componentData)
    setIsEditing(false)
  }

  const handleRichTextChange = (value) => {
    setComponentData({ ...componentData, question: value })
  }

  const handleCorrectAnswerChange = (index) => {
    const newOptions = componentData.options.map((option, i) => ({
      ...option,
      isCorrect: i === index ? !option.isCorrect : option.isCorrect,
    }))
    setComponentData({
      ...componentData,
      options: newOptions,
    })
  }

  return (
    <div>
      <label>
        Question
        <HelpPopover title="Question" content="Enter the checkbox question prompt." />
      </label>
      <CurriculumQuill
        value={componentData.question}
        onChange={handleRichTextChange}
        placeholder={"Enter question here..."}
        style={{ marginBottom: "1rem" }}
      />

      <div>
        <label>
          Options
          <HelpPopover title="Options" content="Add answer choices. Mark all correct answers with the checkbox." />
        </label>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "0" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>
                Option
                <HelpPopover title="Option" content="Enter the text for each answer choice." />
              </th>
              {!componentData.isGradingDisabled && (
                <th style={{ border: "1px solid #ccc", padding: "10px" }}>
                  Correct
                  <HelpPopover title="Correct" content="Check all correct answers for this question." />
                </th>
              )}
              <th style={{ border: "1px solid #ccc", padding: "10px" }}>
                Actions
                <HelpPopover title="Actions" content="Remove an option from the list." />
              </th>
            </tr>
          </thead>
          <tbody>
            {componentData.options.map((option, index) => (
              <tr key={index}>
                <td style={{ border: "1px solid #ccc", padding: "10px" }}>
                  <input
                    placeholder={`Option ${index + 1}`}
                    value={option.option}
                    onChange={(e) => handleOptionChange(e, index)}
                    style={{ width: "100%" }}
                  />
                </td>
                {!componentData.isGradingDisabled && (
                  <td style={{ border: "1px solid #ccc", padding: "10px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      name="correctAnswer"
                      value={option.option}
                      checked={option.isCorrect}
                      onChange={() => handleCorrectAnswerChange(index)}
                    />
                  </td>
                )}
                <td style={{ border: "1px solid #ccc", padding: "10px", textAlign: "center" }}>
                  <WingsButton variant="tertiary" size="mini" onClick={() => removeOption(index)}>
                    Remove
                  </WingsButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <WingsButton variant="tertiary" size="mini" onClick={addOption} style={{ marginTop: "10px" }}>
            + Add Option
          </WingsButton>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <label style={{ marginBottom: "0" }}>
              Disable Grading
              <HelpPopover title="Disable Grading" content="Check to disable grading for this question (no correct answer)." />
            </label>
            <input
              type="checkbox"
              checked={componentData.isGradingDisabled}
              onChange={() => setComponentData({ ...componentData, isGradingDisabled: !componentData.isGradingDisabled })}
            ></input>
          </div>
        </div>
      </div>

      {!isTableComponent && (
        <>
          <hr />
          <label>
            Preview
            <HelpPopover title="Preview" content="See how this checkbox question will appear to students." />
          </label>
          <div className="module-component" style={{ border: "1px solid #ccc", padding: "10px" }}>
            <CheckboxComponent data={componentData} />
          </div>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={isTableComponent ? () => handleTableCellSubmit(componentData) : () => insertComponentAtLocation()}
          style={{ marginTop: "1rem" }}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}

/**
 * Renders the TableComponentCreator, which allows users to create or edit a table component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @returns {JSX.Element} The table component creator.
 */
const TableComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing }) => {
  const { headingHTML, colHeadings, rowHeadings, rows, isBorderless, isRowHeadEnabled, isColHeadEnabled } = initialData || {}

  const [componentData, setComponentData] = useState({
    componentId: "T1",
    type: "TABLE",
    headingHTML: headingHTML,
    headingHTMLEs: "",
    colHeadings: colHeadings ? [...colHeadings] : [""],
    colHeadingsEs: null,
    rowHeadings: rowHeadings ? [...rowHeadings] : [""],
    rowHeadingsEs: null,
    rows: rows ? rows.map((row) => row.map((cell) => ({ ...cell }))) : [[{ componentId: null }]],
    isBorderless: isBorderless ?? false,
    isRowHeadEnabled: isRowHeadEnabled ?? false,
    isColHeadEnabled: isColHeadEnabled ?? true,
  })

  const { addComponent, updateComponent } = useEditorActions()

  const insertComponentAtLocation = () => {
    isEditing ? updateComponent(componentData, coordinates) : addComponent(componentData)
    setIsEditing(false)
  }

  const handleRichTextChange = (value) => {
    setComponentData({ ...componentData, headingHTML: value })
  }

  const [isCellModalOpen, setIsCellModalOpen] = useState(false)
  const [editingCellComponentData, setEditingCellComponentData] = useState(null)
  const [editingCellRowIndex, setEditingCellRowIndex] = useState(null)
  const [editingCellColIndex, setEditingCellColIndex] = useState(null)

  const [numColumns, setNumColumns] = useState(Array.isArray(initialData?.colHeadings) ? initialData.colHeadings.length : 1)
  const [numRows, setNumRows] = useState(Array.isArray(initialData?.rows) ? initialData.rows.length : 1)

  const handleCellSelect = (componentData, rowIndex, colIndex) => {
    setEditingCellComponentData(componentData)
    setEditingCellRowIndex(rowIndex)
    setEditingCellColIndex(colIndex)
    setIsCellModalOpen(true)
  }

  const handleTableCellSubmit = (cellComponentData) => {
    const rowCopy = componentData.rows.map((row) => row.map((cell) => ({ ...cell })))
    rowCopy[editingCellRowIndex].splice(editingCellColIndex, 1, { ...cellComponentData })
    setComponentData({
      ...componentData,
      rows: rowCopy,
    })
    handleTableCellModalClose()
  }

  const handleTableCellModalClose = () => {
    setIsCellModalOpen(false)
    setEditingCellComponentData(null)
    setEditingCellRowIndex(null)
    setEditingCellColIndex(null)
  }

  const handleComponentTypeChange = (e) => {
    setEditingCellComponentData({ componentId: e.target.value })
  }

  const handleBorderlessToggle = () => {
    setComponentData((prev) => ({
      ...prev,
      isBorderless: !prev.isBorderless,
    }))
  }

  const handleRowHeadingsToggle = () => {
    setComponentData((prev) => ({
      ...prev,
      isRowHeadEnabled: !prev.isRowHeadEnabled,
    }))
  }

  const handleColHeadingsToggle = () => {
    setComponentData((prev) => ({
      ...prev,
      isColHeadEnabled: !prev.isColHeadEnabled,
    }))
  }

  const adjustArray = (arr, num, defaultValue = null) => {
    if (num <= arr.length) {
      return arr.slice(0, num)
    } else {
      return arr.concat(new Array(num - arr.length).fill(defaultValue))
    }
  }

  const handleColChange = (newColNum) => {
    setNumColumns(newColNum)
    const updatedRows = componentData.rows.map((row) => adjustArray(row, newColNum, { componentId: null }))
    const updatedHeadings = adjustArray(componentData.colHeadings, newColNum, ``)
    setComponentData({
      ...componentData,
      colHeadings: updatedHeadings,
      rows: updatedRows,
    })
  }

  const handleRowChange = (newRowNum) => {
    setNumRows(newRowNum)
    const updatedRows = adjustArray(
      componentData.rows,
      newRowNum,
      Array.from({ length: numColumns }, () => ({ componentId: null }))
    )
    setComponentData({
      ...componentData,
      rows: updatedRows,
    })
  }

  const [isHeaderModalOpen, setIsHeaderModalOpen] = useState(false)
  const [editingHeaderValue, setEditingHeaderValue] = useState(null)
  const [editingHeaderIndex, setEditingHeaderIndex] = useState(null)
  const [editingHeaderType, setEditingHeaderType] = useState(null)

  const handleHeaderSubmit = () => {
    if (editingHeaderType === "col") {
      const updatedColHeadings = [...componentData.colHeadings]
      updatedColHeadings[editingHeaderIndex] = editingHeaderValue || ``
      setComponentData({
        ...componentData,
        colHeadings: updatedColHeadings,
      })
    } else if (editingHeaderType === "row") {
      const updatedRowHeadings = [...componentData.rowHeadings]
      updatedRowHeadings[editingHeaderIndex] = editingHeaderValue || ``
      setComponentData({
        ...componentData,
        rowHeadings: updatedRowHeadings,
      })
    }
    handleHeaderModalClose()
  }

  const handleHeaderModalClose = () => {
    setIsHeaderModalOpen(false)
    setEditingHeaderValue(null)
    setEditingHeaderIndex(null)
  }

  const handleHeaderSelect = (value, type, index) => {
    setEditingHeaderType(type)
    setEditingHeaderValue(value)
    setEditingHeaderIndex(index)
    setIsHeaderModalOpen(true)
  }

  const renderComponentCreator = () => {
    switch (editingCellComponentData?.componentId) {
      case "QM1":
        return (
          <MultipleChoiceComponentCreator
            initialData={editingCellComponentData}
            setIsEditing={setIsEditing}
            isTableComponent={true}
            coordinates={coordinates}
            handleTableCellSubmit={handleTableCellSubmit}
          />
        )
      case "IR1":
        return (
          <RichTextComponentCreator
            initialData={editingCellComponentData}
            setIsEditing={setIsEditing}
            isTableComponent={true}
            coordinates={coordinates}
            handleTableCellSubmit={handleTableCellSubmit}
          />
        )
      case "QO1":
        return (
          <OpenResponseComponentCreator
            initialData={editingCellComponentData}
            setIsEditing={setIsEditing}
            isTableComponent={true}
            coordinates={coordinates}
            handleTableCellSubmit={handleTableCellSubmit}
          />
        )
      case "QC1":
        return (
          <CheckboxComponentCreator
            initialData={editingCellComponentData}
            setIsEditing={setIsEditing}
            isTableComponent={true}
            coordinates={coordinates}
            handleTableCellSubmit={handleTableCellSubmit}
          />
        )
      case "QD1":
        return (
          <DropdownComponentCreator
            initialData={editingCellComponentData}
            setIsEditing={setIsEditing}
            isTableComponent={true}
            coordinates={coordinates}
            handleTableCellSubmit={handleTableCellSubmit}
          />
        )
      case "II1":
        return (
          <ImageComponentCreator
            initialData={editingCellComponentData}
            setIsEditing={setIsEditing}
            isTableComponent={true}
            coordinates={coordinates}
            handleTableCellSubmit={handleTableCellSubmit}
          />
        )
      case "IF1":
        return (
          <FileDownloadComponentCreator
            initialData={initialData}
            isEditing={isEditing}
            coordinates={coordinates}
            setIsEditing={setIsEditing}
            isTableComponent={true}
            handleTableCellSubmit={handleTableCellSubmit}
          />
        )

      case "IF2":
        return (
          <FileUploadComponentCreator
            initialData={initialData}
            isEditing={isEditing}
            coordinates={coordinates}
            setIsEditing={setIsEditing}
            isTableComponent={true}
            handleTableCellSubmit={handleTableCellSubmit}
          />
        )
      default:
        return null
    }
  }

  return (
    <div>
      <label>
        Text
        <HelpPopover title="Table Description" content="Enter a description or heading for the table." />
      </label>
      <CurriculumQuill
        value={componentData.headingHTML}
        onChange={handleRichTextChange}
        placeholder={"Enter table description here..."}
        style={{ marginBottom: "1rem" }}
      />

      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        <div>
          <label>
            Columns
            <HelpPopover title="Columns" content="Select the number of columns for your table." />
          </label>
          <Form.Control
            as="select"
            className="custom-select-dropdown"
            value={numColumns}
            onChange={(e) => handleColChange(Number(e.target.value))}
            style={{ marginLeft: "0.5rem" }}
          >
            {Array.from({ length: 6 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </Form.Control>
        </div>

        <div>
          <label>
            Rows
            <HelpPopover title="Rows" content="Select the number of rows for your table." />
          </label>
          <Form.Control
            as="select"
            className="custom-select-dropdown"
            value={numRows}
            onChange={(e) => handleRowChange(Number(e.target.value))}
            style={{ marginLeft: "0.5rem" }}
          >
            {Array.from({ length: 25 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </Form.Control>
        </div>

        <div>
          <label>
            Remove Borders
            <HelpPopover title="Remove Borders" content="Toggle to remove borders from the table." />
          </label>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <input type="checkbox" checked={componentData.isBorderless} onChange={handleBorderlessToggle} style={{ margin: "auto" }} />
          </div>
        </div>

        <div>
          <label>
            Row Headings
            <HelpPopover title="Row Headings" content="Toggle to enable row headings in your table." />
          </label>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <input type="checkbox" checked={componentData.isRowHeadEnabled} onChange={handleRowHeadingsToggle} style={{ margin: "auto" }} />
          </div>
        </div>

        <div>
          <label>
            Column Headings
            <HelpPopover title="Column Headings" content="Toggle to enable column headings in your table." />
          </label>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <input type="checkbox" checked={componentData.isColHeadEnabled} onChange={handleColHeadingsToggle} style={{ margin: "auto" }} />
          </div>
        </div>
      </div>

      <label>
        Table Preview (Click Cell To Edit)
        <HelpPopover title="Table Preview" content="Click any cell to edit its content." />
      </label>
      <div className="module-component" style={{ border: "1px solid #ccc", padding: "10px" }}>
        <TableComponent data={componentData} isEditingCell={true} handleCellSelect={handleCellSelect} handleHeaderSelect={handleHeaderSelect} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton variant="primary" size="mini" onClick={insertComponentAtLocation} style={{ marginTop: "1rem" }}>
          {isEditing ? "Update Table" : "Add Table"}
        </WingsButton>
      </div>

      <BootstrapModal show={isCellModalOpen} onHide={() => handleTableCellModalClose()} centered size="lg">
        <BootstrapModal.Header closeButton>
          <h4 style={{ marginBottom: "0" }}>Edit Table Cell</h4>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <label>
            Component Type
            <HelpPopover title="Component Type" content="Select the type of component to add to this cell." />
          </label>
          <Form.Control
            as="select"
            className="custom-select-dropdown"
            id="componentType"
            value={editingCellComponentData?.componentId}
            onChange={handleComponentTypeChange}
          >
            <option value="">(Select One)</option>
            <option value="IR1">Text</option>
            <option value="QM1">Multiple Choice</option>
            <option value="QO1">Open Response</option>
            <option value="QC1">Checkbox</option>
            <option value="QD1">Dropdown</option>
            <option value="II1">Image</option>
          </Form.Control>
          <br />
          <br />
          {renderComponentCreator()}
        </BootstrapModal.Body>
      </BootstrapModal>

      <BootstrapModal show={isHeaderModalOpen} onHide={() => handleHeaderModalClose()} centered size="lg">
        <BootstrapModal.Header closeButton>
          <h4 style={{ marginBottom: "0" }}>Edit {`${editingHeaderType === "col" ? "Column" : "Row"}`} Heading</h4>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <label>
            {editingHeaderType === "col" ? "Column Heading" : "Row Heading"}
            <HelpPopover
              title={editingHeaderType === "col" ? "Column Heading" : "Row Heading"}
              content={`Edit the ${editingHeaderType === "col" ? "column" : "row"} heading for your table.`}
            />
          </label>
          <input
            value={editingHeaderValue || ""}
            onChange={(e) => setEditingHeaderValue(e.target.value)}
            placeholder={`${editingHeaderType === "col" ? "Column Heading" : "Row Heading"}`}
            style={{ width: "100%" }}
          />

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <WingsButton variant="primary" size="mini" onClick={handleHeaderSubmit} style={{ marginTop: "1rem" }}>
              Update Heading
            </WingsButton>
          </div>
        </BootstrapModal.Body>
      </BootstrapModal>
    </div>
  )
}

/**
 * Renders a file download component.
 * @param {Object} props.data - Data for the file download.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Object} props.cellCoordinates - Coordinates of the component in a table cell (if applicable).
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @returns {JSX.Element} The file download component.
 */
export const FileDownloadComponent = ({ data, coordinates = {}, cellCoordinates = {}, isTableComponent }) => {
  const { headingHTML, headingHTMLEs, fileName, fileUrl } = data || {}
  const { locale } = useIntl()

  const cleanLocalizedHeadingHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, headingHTMLEs, headingHTML))

  const handleDownload = () => {
    if (fileUrl) {
      const link = document.createElement("a")
      link.href = fileUrl
      link.download = fileName || "download"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  return (
    <div className="module-component-file-download">
      {hasHTMLContent(cleanLocalizedHeadingHTML) && (
        <div dangerouslySetInnerHTML={{ __html: cleanLocalizedHeadingHTML }} className="module-component-heading" />
      )}

      <div
        className="file-download-container"
        style={{
          border: "2px dashed #ccc",
          borderRadius: "8px",
          padding: "20px",
          textAlign: "center",
          backgroundColor: "#f9f9f9",
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
        onClick={handleDownload}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#40777c"
          e.currentTarget.style.backgroundColor = "#c1e1e4"
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "#ccc"
          e.currentTarget.style.backgroundColor = "#f9f9f9"
        }}
      >
        <div style={{ marginBottom: "10px" }}>
          <FontAwesomeIcon icon={faDownload} size="2x" color="#40777c" />
        </div>
        <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "5px" }}>{fileName || "Download File"}</div>
        <div style={{ fontSize: "14px", marginTop: "10px" }}>Click to download</div>
      </div>
    </div>
  )
}

const FileDownloadComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing, isTableComponent, handleTableCellSubmit }) => {
  const { addComponent, updateComponent } = useEditorActions()
  const { firebase } = useContext(FirebaseContext)
  const { headingHTML, fileName, fileUrl, uuid } = initialData || {}

  const [componentData, setComponentData] = useState({
    headingHTML: headingHTML ?? "",
    fileName: fileName ?? "",
    fileUrl: fileUrl ?? "",
  })

  const [isUploading, setIsUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const componentPayload = {
    componentId: "IF1",
    type: "INSTRUCTIONAL",
    headingHTML: componentData.headingHTML,
    headingHTMLEs: "",
    fileName: componentData.fileName,
    fileUrl: componentData.fileUrl,
    uuid: uuid || uuidv4(), // Generate a new UUID if not provided
  }

  const insertComponentAtLocation = () => {
    if (!componentPayload.fileUrl) {
      window.alert("Please provide a file URL")
      return
    }
    isEditing ? updateComponent(componentPayload, coordinates) : addComponent(componentPayload)
    setIsEditing(false)
  }

  const handleInputChange = (e, field) => {
    setComponentData({
      ...componentData,
      [field]: e.target.value,
    })
  }

  // Handle file upload using the same pattern as useUserFiles
  const handleFileUpload = async (file) => {
    if (!file) return
    setIsUploading(true)
    setUploadProgress(0)

    try {
      // Generate a unique file ID similar to useUserFiles

      // replace with UUID associated with component
      const fileId = componentPayload.uuid
      const folderName = "curriculum-hosted-files"

      // Upload the file using the same Firebase method as useUserFiles
      await firebase.uploadUserFileWithClear(folderName, fileId, file, null)

      // Get the file URL after upload
      const fileURL = await firebase.getUserFileURL(null, folderName, fileId)

      if (fileURL) {
        setComponentData((prev) => ({
          ...prev,
          fileName: file.name,
          fileUrl: fileURL,
        }))
      } else {
        throw new Error("No file URL returned after upload")
      }
    } catch (error) {
      console.error("Error uploading file:", error)
      window.alert(`Error uploading file: ${error.message || error}`)
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0])
    }
  }

  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0])
    }
  }

  return (
    <div className="file-download-component-creator">
      <label>
        Heading
        <HelpPopover title="Heading" content="Enter a heading for the file download (optional)." />
      </label>
      <CurriculumQuill
        value={componentData.headingHTML}
        onChange={(newHeading) => setComponentData({ ...componentData, headingHTML: newHeading })}
        placeholder={"Enter heading here..."}
        style={{ marginBottom: "1rem" }}
      />

      <label>
        File Upload
        <HelpPopover title="File Upload" content="Upload a file from your computer or drag and drop a file into the upload area." />
      </label>

      {/* File Upload Area */}
      <div
        className={`file-upload-area ${dragActive ? "drag-active" : ""}`}
        style={{
          border: `2px dashed ${dragActive ? "#40777c" : "#ccc"}`,
          borderRadius: "8px",
          padding: "40px 20px",
          textAlign: "center",
          backgroundColor: dragActive ? "#f0f8ff" : "#f9f9f9",
          cursor: "pointer",
          transition: "all 0.2s ease",
          marginBottom: "1rem",
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById("file-input").click()}
      >
        {isUploading ? (
          <div>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#40777c" />
            <p>Uploading file...</p>
            {uploadProgress > 0 && (
              <div style={{ width: "200px", margin: "10px auto" }}>
                <div
                  style={{
                    width: "100%",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "10px",
                    height: "10px",
                  }}
                >
                  <div
                    style={{
                      width: `${uploadProgress}%`,
                      backgroundColor: "#40777c",
                      height: "100%",
                      borderRadius: "10px",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <FontAwesomeIcon icon={faUpload} size="2x" color="#40777c" style={{ marginBottom: "10px" }} />
            <p style={{ margin: "10px 0", fontSize: "16px", fontWeight: "bold" }}>Drop files here or click to upload</p>
            <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>Supports all file types</p>
          </div>
        )}
      </div>

      <input id="file-input" type="file" onChange={handleFileInputChange} style={{ display: "none" }} disabled={isUploading} />

      <label>
        File Name
        <HelpPopover title="File Name" content="Enter the display name for the file (auto-filled when uploaded)." />
      </label>
      <input
        placeholder="File Name"
        value={componentData.fileName}
        onChange={(e) => handleInputChange(e, "fileName")}
        style={{ marginBottom: "1rem" }}
      />

      <label>
        File URL
        <HelpPopover title="File URL" content="The URL where the file is hosted (auto-filled when uploaded, or enter manually)." />
      </label>
      <input
        placeholder="File URL"
        value={componentData.fileUrl}
        onChange={(e) => handleInputChange(e, "fileUrl")}
        style={{ marginBottom: "1rem" }}
      />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={isTableComponent ? () => handleTableCellSubmit(componentPayload) : () => insertComponentAtLocation()}
          style={{ marginTop: "1rem" }}
          disabled={isUploading}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}

/**
 * Renders a file upload component for students to upload files.
 * @param {Object} props.data - Data for the file upload.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Object} props.cellCoordinates - Coordinates of the component in a table cell (if applicable).
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @returns {JSX.Element} The file upload component.
 */
export const FileUploadComponent = ({ data, coordinates = {}, cellCoordinates = {}, isTableComponent }) => {
  const { headingHTML, headingHTMLEs, allowedFileTypes, maxFileSize, isRequired, uuid } = data || {}
  const { locale } = useIntl()
  const { firebase } = useContext(FirebaseContext)
  const { dispatch, moduleUI, moduleId } = useModuleContext()
  const { mode } = moduleUI
  const { slideIndex, componentIndex, sectionIndex } = coordinates

  const [isUploading, setIsUploading] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [showUploadArea, setShowUploadArea] = useState(false) // New state for showing upload area

  const uploadedFileExtension = uploadedFile ? uploadedFile.fileName.split(".").pop().toLowerCase() : ""
  const isVideoFile = ["mp4", "mov", "avi", "mkv"].includes(uploadedFileExtension)
  const isImageFile = ["jpg", "jpeg", "png", "gif"].includes(uploadedFileExtension)

  const cleanLocalizedHeadingHTML = DOMPurify.sanitize(getLocalizedTextWithFallback(locale, headingHTMLEs, headingHTML))

  const fileUploadPath = `curriculum-uploaded-files/${moduleId}`

  const handleFileUpload = async (file) => {
    if (!file) return

    // Check file type if restrictions exist
    if (allowedFileTypes && allowedFileTypes.length > 0) {
      const fileExtension = file.name.split(".").pop().toLowerCase()
      if (!allowedFileTypes.includes(fileExtension)) {
        window.alert(`File type not allowed. Allowed types: ${allowedFileTypes.join(", ")}`)
        return
      }
    }

    // Check file size if limit exists
    if (maxFileSize && file.size > maxFileSize * 1024 * 1024) {
      window.alert(`File too large. Maximum size: ${maxFileSize}MB`)
      return
    }

    setIsUploading(true)

    try {
      const fileId = uuid

      // Use uploadUserFileWithClear to replace any existing file
      await firebase.uploadUserFileWithClear(fileUploadPath, fileId, file, null)
      const fileURL = await firebase.getUserFileURL(null, fileUploadPath, fileId)

      const uploadData = {
        fileName: file.name,
        fileUrl: fileURL,
        fileSize: file.size,
        uploadDate: new Date().toISOString(),
      }

      setUploadedFile(uploadData)
      setShowUploadArea(false) // Hide upload area after successful upload

      // Record the upload as a response
      dispatch({
        type: "RECORD_RESPONSE",
        slideIndex,
        componentIndex,
        sectionIndex,
        isTableComponent,
        cellCoordinates,
        payload: uploadData,
      })
    } catch (error) {
      console.error("Error uploading file:", error)
      window.alert("Error uploading file")
    } finally {
      setIsUploading(false)
    }
  }

  const handleReplaceFile = () => {
    setShowUploadArea(true)
  }

  const handleCancelReplace = () => {
    setShowUploadArea(false)
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0])
    }
  }

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0])
    }
    // Clear the input value to allow selecting the same file again
    e.target.value = ""
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return ""
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i]
  }

  const getUploadedFile = async (fileId) => {
    if (!fileId) {
      console.warn("No file ID provided for fetching uploaded file")
      return
    }

    try {
      const fileMetadata = await firebase.getUserFile(null, fileUploadPath, fileId)
      const fileURL = await firebase.getUserFileURL(null, fileUploadPath, fileId)

      if (!fileMetadata || !fileURL) {
        return
      }

      const uploadData = {
        fileName: fileMetadata.name,
        fileUrl: fileURL,
        fileSize: fileMetadata.size,
        uploadDate: fileMetadata.timeCreated,
      }

      setUploadedFile(uploadData)
    } catch (error) {
      console.error("Error fetching uploaded file:", error)
    }
  }

  useEffect(() => {
    getUploadedFile(uuid)
  }, [uuid])

  return (
    <div className="module-component-file-upload">
      {hasHTMLContent(cleanLocalizedHeadingHTML) && (
        <div dangerouslySetInnerHTML={{ __html: cleanLocalizedHeadingHTML }} className="module-component-heading" />
      )}

      {mode === "STUDENT" && (
        <div>
          {uploadedFile && !showUploadArea ? (
            <div
              style={{
                border: "2px solid #50A097",
                borderRadius: "8px",
                padding: "20px",
                textAlign: "center",
                backgroundColor: "#f0f8f0",
              }}
            >
              {isVideoFile && (
                <video width="100%" controls>
                  <source src={uploadedFile.fileUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}

              {isImageFile && <img src={uploadedFile.fileUrl} alt={uploadedFile.fileName} style={{ maxWidth: "100%", marginTop: "1rem" }} />}

              <FontAwesomeIcon icon={faCheckCircle} size="2x" color="#50A097" style={{ marginBottom: "10px" }} />
              <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "5px" }}>File Uploaded Successfully</div>
              <div style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
                {uploadedFile.fileName} ({formatFileSize(uploadedFile.fileSize)})
              </div>
              <button
                onClick={handleReplaceFile}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#40777c",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#335a5f")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#40777c")}
              >
                Replace File
              </button>
            </div>
          ) : (
            <div>
              <div
                style={{
                  border: `2px dashed ${dragActive ? "#40777c" : "#ccc"}`,
                  borderRadius: "8px",
                  padding: "40px 20px",
                  textAlign: "center",
                  backgroundColor: dragActive ? "#f0f8ff" : "#f9f9f9",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`file-upload-${componentIndex}`).click()}
              >
                {isUploading ? (
                  <div>
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#40777c" />
                    <p>Uploading file...</p>
                  </div>
                ) : (
                  <div>
                    <FontAwesomeIcon icon={faUpload} size="2x" color="#40777c" style={{ marginBottom: "10px" }} />
                    <p style={{ margin: "10px 0", fontSize: "16px", fontWeight: "bold" }}>
                      {uploadedFile ? "Upload file here to replace" : "Upload file here"}
                    </p>
                    {allowedFileTypes && (
                      <p style={{ margin: "5px 0", fontSize: "14px", color: "#666" }}>Allowed types: {allowedFileTypes.join(", ")}</p>
                    )}
                    {maxFileSize && <p style={{ margin: "5px 0", fontSize: "14px", color: "#666" }}>Max size: {maxFileSize}MB</p>}
                  </div>
                )}
              </div>

              {uploadedFile && showUploadArea && (
                <div style={{ marginTop: "10px", textAlign: "center" }}>
                  <button
                    onClick={handleCancelReplace}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#5a6268")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#6c757d")}
                  >
                    Cancel
                  </button>
                </div>
              )}

              <input
                id={`file-upload-${componentIndex}`}
                type="file"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
                disabled={isUploading}
                accept={allowedFileTypes ? allowedFileTypes.map((type) => `.${type}`).join(",") : undefined}
              />
            </div>
          )}
        </div>
      )}

      {(mode === "EDITOR" || mode === "PREVIEW") && (
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "20px",
            textAlign: "center",
            backgroundColor: "#f9f9f9",
          }}
        >
          <FontAwesomeIcon icon={faUpload} size="2x" color="#ccc" style={{ marginBottom: "10px" }} />
          <div style={{ fontSize: "16px", color: "#666" }}>File Upload Component (Student View Only)</div>
        </div>
      )}

      {mode === "SUBMISSION" && uploadedFile && (
        <>
          {isVideoFile && (
            <video width="700px" controls>
              <source src={uploadedFile.fileUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}

          {isImageFile && <img src={uploadedFile.fileUrl} alt={uploadedFile.fileName} style={{ maxWidth: "100%", marginTop: "1rem" }} />}

          <div style={{ marginTop: "1rem", textAlign: "center" }}>
            <button
              onClick={() => {
                window.open(uploadedFile.fileUrl, "_blank", "noopener,noreferrer")
              }}
              style={{
                padding: "8px 16px",
                backgroundColor: "#40777c",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#335a5f")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#40777c")}
            >
              Download Uploaded File
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const FileUploadComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing, isTableComponent, handleTableCellSubmit }) => {
  const { addComponent, updateComponent } = useEditorActions()
  const { headingHTML, allowedFileTypes, maxFileSize, isRequired } = initialData || {}

  const [componentData, setComponentData] = useState({
    headingHTML: headingHTML ?? "",
    allowedFileTypes: allowedFileTypes ?? [],
    maxFileSize: maxFileSize ?? 10, // Default 10MB
    isRequired: isRequired ?? false,
  })

  const [newFileType, setNewFileType] = useState("")

  const componentPayload = {
    componentId: "IF2",
    type: "INSTRUCTIONAL",
    headingHTML: componentData.headingHTML,
    headingHTMLEs: "",
    allowedFileTypes: componentData.allowedFileTypes,
    maxFileSize: componentData.maxFileSize,
    isRequired: componentData.isRequired,
  }

  const insertComponentAtLocation = () => {
    isEditing ? updateComponent(componentPayload, coordinates) : addComponent(componentPayload)
    setIsEditing(false)
  }

  const addFileType = () => {
    if (newFileType && !componentData.allowedFileTypes.includes(newFileType)) {
      setComponentData({
        ...componentData,
        allowedFileTypes: [...componentData.allowedFileTypes, newFileType.toLowerCase()],
      })
      setNewFileType("")
    }
  }

  const removeFileType = (typeToRemove) => {
    setComponentData({
      ...componentData,
      allowedFileTypes: componentData.allowedFileTypes.filter((type) => type !== typeToRemove),
    })
  }

  const commonFileTypes = ["pdf", "doc", "docx", "txt", "jpg", "jpeg", "png", "gif", "mp4", "mp3", "zip"]

  return (
    <div className="file-upload-component-creator">
      <label>
        Heading
        <HelpPopover title="Heading" content="Enter a heading for the file upload prompt." />
      </label>
      <CurriculumQuill
        value={componentData.headingHTML}
        onChange={(newHeading) => setComponentData({ ...componentData, headingHTML: newHeading })}
        placeholder={"Enter heading here..."}
        style={{ marginBottom: "1rem" }}
      />

      <label>
        Allowed File Types
        <HelpPopover title="Allowed File Types" content="Specify which file types students can upload. Leave empty to allow all types." />
      </label>
      <div style={{ marginBottom: "1rem" }}>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
          <input placeholder="File type (e.g., pdf, jpg)" value={newFileType} onChange={(e) => setNewFileType(e.target.value)} style={{ flex: 1 }} />
          <WingsButton variant="secondary" size="mini" onClick={addFileType}>
            Add
          </WingsButton>
        </div>

        <div style={{ marginBottom: "0.5rem" }}>
          <strong>Common types:</strong>
          {commonFileTypes.map((type) => (
            <button
              key={type}
              onClick={() => {
                if (!componentData.allowedFileTypes.includes(type)) {
                  setComponentData({
                    ...componentData,
                    allowedFileTypes: [...componentData.allowedFileTypes, type],
                  })
                }
              }}
              style={{
                margin: "0.25rem",
                padding: "0.25rem 0.5rem",
                border: "1px solid #ccc",
                borderRadius: "4px",
                background: componentData.allowedFileTypes.includes(type) ? "#d4fb44" : "white",
                cursor: "pointer",
              }}
            >
              {type}
            </button>
          ))}
        </div>

        {componentData.allowedFileTypes.length > 0 && (
          <div>
            <strong>Selected types:</strong>
            {componentData.allowedFileTypes.map((type) => (
              <span
                key={type}
                style={{
                  display: "inline-block",
                  margin: "0.25rem",
                  padding: "0.25rem 0.5rem",
                  background: "#f0f0f0",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
                onClick={() => removeFileType(type)}
              >
                {type} ×
              </span>
            ))}
          </div>
        )}
      </div>

      <label>
        Maximum File Size (MB)
        <HelpPopover title="Maximum File Size" content="Set the maximum file size allowed for uploads in megabytes." />
      </label>
      <input
        type="number"
        min="1"
        max="100"
        value={componentData.maxFileSize}
        onChange={(e) => setComponentData({ ...componentData, maxFileSize: parseInt(e.target.value) })}
        style={{ marginBottom: "1rem", width: "100px" }}
      />

      <div style={{ marginBottom: "1rem" }}>
        <label>
          Required
          <HelpPopover title="Required" content="Check if this file upload is required for completion." />
          <input
            type="checkbox"
            checked={componentData.isRequired}
            onChange={(e) => setComponentData({ ...componentData, isRequired: e.target.checked })}
            style={{ marginLeft: "0.5rem" }}
          />
        </label>
      </div>

      {!isTableComponent && (
        <>
          <label>
            Preview
            <HelpPopover title="Preview" content="See how this file upload will appear to students." />
          </label>
          <div className="module-component" style={{ border: "1px solid #ccc", padding: "10px" }}>
            <FileUploadComponent data={componentData} />
          </div>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={isTableComponent ? () => handleTableCellSubmit(componentPayload) : () => insertComponentAtLocation()}
          style={{ marginTop: "1rem" }}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}

/**
 * Renders the CompletionAnimationComponentCreator, which allows users to create or edit a completion animation component.
 * @param {Object} props.initialData - Initial data for the component (if editing).
 * @param {boolean} props.isEditing - Whether the component is being edited.
 * @param {Object} props.coordinates - Coordinates of the component in the module.
 * @param {Function} props.setIsEditing - Function to toggle editing mode.
 * @param {boolean} props.isTableComponent - Whether the component is part of a table.
 * @param {Function} props.handleTableCellSubmit - Function to handle table cell submission.
 * @returns {JSX.Element} The completion animation component creator.
 */
const CompletionAnimationComponentCreator = ({ initialData, isEditing, coordinates, setIsEditing, isTableComponent, handleTableCellSubmit }) => {
  const { addComponent, updateComponent } = useEditorActions()
  const { src, headingHTML, fileName, loop, speed } = initialData || {}
  const [componentData, setComponentData] = useState({
    src: src ?? "",
    fileName: fileName ?? (Object.keys(completionAnimationLibraryMap)[0] || ""),
    headingHTML: headingHTML ?? "",
    loop: loop ?? true,
    speed: speed ?? 1,
  })

  const componentPayload = {
    componentId: "IA2",
    type: "INSTRUCTIONAL",
    headingHTML: componentData.headingHTML,
    fileName: componentData.fileName,
    headingHTMLEs: "",
    loop: componentData.loop,
    speed: componentData.speed,
  }

  const insertComponentAtLocation = async () => {
    isEditing ? updateComponent(componentPayload, coordinates) : addComponent(componentPayload)
    setIsEditing(false)
  }

  const handleSelectAnimation = (animationKey) => {
    setComponentData({ ...componentData, fileName: animationKey })
  }

  // Predefined completion messages
  const predefinedMessages = [
    {
      label: "You did it! This section's a wrap. Let's keep it rolling.",
      value: "<h2 class='ql-align-center'>You did it! This section's a wrap. Let's keep it rolling.</h2>",
    },
    { label: "Boom! That section's done. Onward?", value: "<h2 class='ql-align-center'>Boom! That section's done. Onward?</h2>" },
    {
      label: "Section complete—great job! Keep the progress going with the next one.",
      value: "<h2 class='ql-align-center'>Section complete—great job! Keep the progress going with the next one.</h2>",
    },
    { label: "Custom Message", value: "custom" },
  ]

  const [selectedMessage, setSelectedMessage] = useState(() => {
    // Find matching predefined message or default to custom
    const matchingMessage = predefinedMessages.find((msg) => msg.value === componentData.headingHTML)
    return matchingMessage ? matchingMessage.value : "custom"
  })

  const [showCustomInput, setShowCustomInput] = useState(() => {
    // Show custom input if no predefined message matches
    return !predefinedMessages.find((msg) => msg.value === componentData.headingHTML && msg.value !== "custom")
  })

  const handleMessageChange = (e) => {
    const selectedValue = e.target.value
    setSelectedMessage(selectedValue)

    if (selectedValue === "custom") {
      setShowCustomInput(true)
      // Keep current headingHTML when switching to custom
    } else {
      setShowCustomInput(false)
      setComponentData({ ...componentData, headingHTML: selectedValue })
    }
  }

  const handleCustomMessageChange = (value) => {
    setComponentData({ ...componentData, headingHTML: value })
  }

  return (
    <div className="completion-animation-component-creator">
      <label>
        Completion Message
        <HelpPopover title="Completion Message" content="Select a predefined completion message or choose custom to create your own." />
      </label>
      <Form.Control
        as="select"
        className="custom-select-dropdown"
        value={selectedMessage}
        onChange={handleMessageChange}
        style={{ marginBottom: "1rem" }}
      >
        {predefinedMessages.map((message, index) => (
          <option key={index} value={message.value}>
            {message.label}
          </option>
        ))}
      </Form.Control>

      {showCustomInput && (
        <div style={{ marginBottom: "1rem" }}>
          <label>
            Custom Message
            <HelpPopover title="Custom Message" content="Enter your custom completion message with formatting." />
          </label>
          <CurriculumQuill
            value={componentData.headingHTML}
            onChange={handleCustomMessageChange}
            placeholder={"Enter custom completion message..."}
            configType="COMPLETION_HEADING"
          />
        </div>
      )}

      <label>
        Animation Library
        <HelpPopover title="Animation Library" content="Select a completion animation from the library below." />
      </label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
          gap: "12px",
          margin: "12px 0",
        }}
      >
        {Object.keys(completionAnimationLibraryMap).map((animationKey) => (
          <AnimationComponentPreviewCard
            key={animationKey}
            animationKey={animationKey}
            isSelected={componentData.fileName === animationKey}
            handleSelectAnimation={handleSelectAnimation}
          />
        ))}
      </div>
      <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1rem" }}>
        <label>
          Loop
          <HelpPopover title="Loop" content="Toggle whether the completion animation should loop continuously." />
          <input
            type="checkbox"
            checked={componentData.loop}
            onChange={(e) => setComponentData({ ...componentData, loop: e.target.checked })}
            style={{ marginLeft: "0.5rem" }}
            className="module-component-checkbox"
          />
        </label>
        <label>
          Speed
          <HelpPopover title="Speed" content="Set the playback speed for the completion animation." />
          <Form.Control
            as="select"
            value={componentData.speed}
            onChange={(e) => setComponentData({ ...componentData, speed: Number(e.target.value) })}
            style={{ marginLeft: "0.5rem", width: "120px", display: "inline-block" }}
            className="custom-select-dropdown"
          >
            <option value={0.25}>0.25x</option>
            <option value={0.5}>0.5x</option>
            <option value={1}>1x</option>
            <option value={1.5}>1.5x</option>
            <option value={2}>2x</option>
          </Form.Control>
        </label>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <WingsButton
          variant="primary"
          size="mini"
          onClick={isTableComponent ? () => handleTableCellSubmit(componentPayload) : () => insertComponentAtLocation()}
          style={{ marginTop: "1rem" }}
        >
          {isEditing ? "Update Component" : "Add Component"}
        </WingsButton>
      </div>
    </div>
  )
}
